import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { app, db, storage } from '../../../firebaseConfig';
import {
  doc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  Timestamp,
  getDoc,
  addDoc,
  serverTimestamp,
  writeBatch,

} from 'firebase/firestore';
import { getStorage, ref, getDownloadURL, uploadBytes, deleteObject, getMetadata } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import SideNavbar from '../../../components/SideNavbar';
import { v4 as uuidv4 } from 'uuid';
import { useAppSelector } from '../../../redux/store';
import TableSkeleton from '../../../components/TableSkeleton';

// firebase
import { fetchCompanyUsers, fetchStaffRoles, checkLicensesLimit, fetchSubAgents, updateSubAgent, fetchDepartments, fetchSubAgentPermissions, fetchPipelines, fetchFilterButtons } from './Function/LeadFunctions'
import Toast from '../../../components/Toast';
import { useAuth } from '../../../AuthContext';

type AccessLevel = "No Access" | "Partial Access" | "Full Access";

interface UserData {
  id: string;
  name: string;
  email: string;
  phone: string;
  photo: string;
  staffRoleId?: string;
  departmentId?: string;
  image?: string;
  isActive?: boolean;
  isAdmin?: boolean;
  isManager?: boolean;
  isStaff?: boolean;
  managerId?: string;
  managerIds?: string[]; // Array of manager IDs
  callAttemptTarget?: number;
  pipelineIds?: string[]; // Assuming this is an array of pipeline IDs
  filterButtons?: string[]; // Assuming this is an array of filter button names
  lastAppVisitTimestamp?: any; // Firestore timestamp field
  leftSidePanelItems?: string[];
}
interface SubUserData {
  id: string;
  name: string;
  username: string;
  email: string;
  phone: string;
  permissionId?: string;
  isActive?: boolean;
}

interface FormData {
  photo: string;
  id: string;
  name: string;
  email: string;
  phone: string;
  staffRoleId: string;
  departmentId: string;
  password: string;
  isAdmin: boolean;
  isManager: boolean;
  isStaff: boolean;
  managerId: string;
  managerIds: string[];
  callAttemptTarget?: number;
  pipelineIds?: string[]; // Assuming this is an array of pipeline IDs
  filterButtons?: string[]; // Assuming this is an array of filter button names
  leftSidePanelItems?: string[];
};
interface SubFormData {
  id: string;
  name: string;
  username: string;
  password: string;
  email?: string;
  phone: string;
  permissionId: string;
};
interface Department {
  id: string;
  name: string;
  staff: UserData[];
}

interface DepartmentData {
  id: string;
  department: string;
  tabPermissions: {
    name: string;
    read: boolean;
    write: boolean;
  }[];
  actionPermission?: {
    name: string;
    allowed: boolean;
  }[];
  bulkActionPermission?: {
    name: string;
    id: number;
    active: boolean;
    view: boolean;
  }[];
  sidebarNavigation?: {
    id: number;
    name: string;
    normalIcon: string;
    activeIcon: string;
    link: string | object[];
    isActive: boolean;
    isVisible: boolean;
  }[];
}

interface UserCreationResult {
  data: {
    isActive: boolean;
    id?: string;
    message?: string;
  };
}

interface FetchStaffRolesResult {
  roles: { [key: string]: string };
}

interface FetchCompanyUsersResult {
  users: UserData[];
  subAgents: SubUserData[];
  companyId: string;
}

interface FetchDepartmentsResult {
  departments: { id: string, name: string }[];
}
interface FetchSubAgentPermissionsResult {
  subAgentPermissions: { id: string, name: string }[];
}
interface PhotoChanges {
  action: 'upload' | 'delete' | null;
  file?: File;
  fileName?: string;
}

interface LeftSidePanelItem {
  id: string;
  name: string;
  isActive: boolean;
  link: string | string[];
}


const StaffManagement: React.FC = () => {

  const { currentUser } = useAuth();

  const [isDisabled, setIsDisabled] = useState(false);
  const [copySuccess, setCopySuccess] = useState("");
  const [fadeOut, setFadeOut] = useState(false);
  const [isAddPopupOpen, setAddPopupOpen] = useState(false);
  const [isSubAddPopupOpen, setSubAddPopupOpen] = useState(false);
  const [isEditPopupOpen, setEditPopupOpen] = useState(false);
  const [isSubEditPopupOpen, setSubEditPopupOpen] = useState(false);
  const [emailResetPopupOpen, setEmailResetPopupOpen] = useState(false);
  const [subEmailResetPopupOpen, setSubEmailResetPopupOpen] = useState(false);
  const [passwordResetPopupOpen, setPasswordResetPopupOpen] = useState(false);
  const [subPasswordResetPopupOpen, setSubPasswordResetPopupOpen] = useState(false);
  const [updatedEmail, setUpdatedEmail] = useState('');
  const [updatedPassword, setUpdatedPassword] = useState('');
  const [userData, setUserData] = useState<UserData[]>([]);
  const [subAgentsData, setSubAgentsData] = useState<SubUserData[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const auth = getAuth(app);
  const [staffRoles, setStaffRoles] = useState<{ [key: string]: string }>({});
  const [departments, setDepartments] = useState<{ id: string, name: string }[]>([]);
  const [subAgentPermissions, setSubAgentPermissions] = useState<{ id: string, name: string }[]>([]);
  const [accessLevels, setAccessLevels] = useState<{ [key: string]: AccessLevel }>({});
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [loginPostfix, setLoginPostfix] = useState<string>('');
  const [licencesLimit, setLicencesLimit] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canAddMoreUsers, setCanAddMoreUsers] = useState<boolean>(true);
  const [activeUsersCount, setActiveUsersCount] = useState<number | null>(null);
  const [pipelines, setPipelines] = useState<{ id: string, name: string }[]>([]);
  const [filterButtons, setFilterButtons] = useState<{ id: string, name: string, color: string }[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isErrorToast, setIsErrorToast] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    id: '',
    name: '',
    email: '',
    phone: '',
    staffRoleId: '',
    departmentId: '',
    password: '',
    isAdmin: false,
    isManager: false,
    isStaff: false,
    managerId: '',
    managerIds: [],
    callAttemptTarget: 0,
    pipelineIds: [],
    filterButtons: [],
    leftSidePanelItems: [],
    photo: '', // <-- add this
  });
  const [photoUrl, setPhotoUrl] = useState<string>('');
  const [photoName, setPhotoName] = useState<string>('No file selected');
  const [uploadTime, setUploadTime] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profilePhoto, setProfilePhoto] = useState<string>('');
  // Add these states near your other state declarations
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [pendingPhotoChanges, setPendingPhotoChanges] = useState<PhotoChanges>({ action: null });

  const [pendingUser, setPendingUser] = useState<UserData | null>(null);
  const [pendingSubUser, setPendingSubUser] = useState<SubUserData | null>(null);


  const [departmentsWithStaff, setDepartmentsWithStaff] = useState<Department[]>([]);
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());

  const [leftSidePanelItems, setLeftSidePanelItems] = useState<LeftSidePanelItem[]>([]);





  const inputRef = useRef<HTMLInputElement>(null);
  const [subFormData, setSubFormData] = useState<SubFormData>({
    id: '',
    name: '',
    username: '',
    password: '',
    email: '',
    phone: '',
    permissionId: '',
  });

  const functions = getFunctions(app);
  const createUserFunction = httpsCallable(functions, 'createUserWithAdminAuth');
  const createSubAgentFunction = httpsCallable(functions, 'createSubAgentWithAdminAuth');

  const [passwordShown, setPasswordShown] = useState("password");
  const toogleEyeButton = () => {
    setPasswordShown(passwordShown === "text" ? "password" : "text");
  };

  const showToastMessage = (message: string, isError: boolean = false) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };


  const fetchInitialData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [
        staffRolesResult,
        companyUsersResult,
        licencesLimitResult,
        subAgentsResult,
        departmentResult,
        subAgentPermissionsResults,
        companyPipelines,
        filterButtons,
        panelItemsResult
      ] = await Promise.all([
        fetchStaffRoles(db, auth.currentUser),
        fetchCompanyUsers(db, auth.currentUser),
        checkLicensesLimit(db, auth.currentUser),
        fetchSubAgents(db, auth.currentUser),
        fetchDepartments(db, auth.currentUser),
        fetchSubAgentPermissions(db, auth.currentUser),
        fetchPipelines(db, auth.currentUser),
        fetchFilterButtons(db, auth.currentUser),
        fetchLeftSidePanelItems(db, auth.currentUser)
      ]);

      console.group("Details >>>>>>>>>>>>>>");
      console.log("staffRoles:", staffRoles);
      console.log("companyUsersResult:", companyUsersResult);
      console.log("licencesLimitResult:", licencesLimitResult);
      console.log("subAgentsResult:", subAgentsResult);
      console.log("departmentResult:", departmentResult);
      console.log("subAgentPermissionsResults:", subAgentPermissionsResults);
      console.log("companyPipelines:", companyPipelines);
      console.log("filterButtons:", filterButtons);
      console.groupEnd();



      const rolesData = staffRolesResult.data as FetchStaffRolesResult;
      setStaffRoles(rolesData.roles || {});

      // const initialAccessLevels: { [key: string]: AccessLevel } = Object.keys(rolesData.roles || {}).reduce((acc, key) => {
      //   acc[(rolesData.roles || {})[key]] = "No Access";
      //   return acc;
      // }, {} as { [key: string]: AccessLevel });

      const usersData = companyUsersResult.data as FetchCompanyUsersResult;
      const subAgentsData = subAgentsResult.data as FetchCompanyUsersResult;
      const licenceData = licencesLimitResult.data as {
        loginPostfix: string,
        licencesLimit: number,
        activeUsersCount: number,
        canAddMoreUsers: boolean
      };

      console.group("User and Company license>>>>>>>>>>>>>>");
      console.log("usersData:", usersData);
      console.log("subAgentsData:", subAgentsData);
      console.log("licenceData:", licenceData);
      console.groupEnd();

      const departmentsData = departmentResult.data as unknown as FetchDepartmentsResult;
      const subAgentPermissionsData = subAgentPermissionsResults.data as FetchSubAgentPermissionsResult;

      console.group("Department Details>>>>>>>>>>>>>>>>>>>>>");
      console.log("departmentsData:", departmentsData.departments);
      console.log("subAgentPermissionsData:", subAgentPermissionsData);
      console.groupEnd();

      // Group staff by department
      const groupedDepartments = groupStaffByDepartment(usersData.users, departmentsData.departments);

      console.log("Grouped Departments", groupedDepartments);

      const panelItemsData = panelItemsResult.data as { panelItems: LeftSidePanelItem[] };
      setLeftSidePanelItems(panelItemsData.panelItems.filter(item => item.isActive));


      setDepartmentsWithStaff(groupedDepartments);
      setDepartments(departmentsData.departments);
      setSubAgentPermissions(subAgentPermissionsData.subAgentPermissions);
      // setStaffRoles(roles);
      // setAccessLevels(initialAccessLevels);
      setCompanyId(usersData.companyId);
      setUserData(usersData.users);
      setSubAgentsData(subAgentsData.subAgents);
      setLoginPostfix('@' + licenceData.loginPostfix);
      setLicencesLimit(licenceData.licencesLimit);
      setActiveUsersCount(licenceData.activeUsersCount);
      setCanAddMoreUsers(licenceData.canAddMoreUsers);
      setPipelines(companyPipelines.data.pipelines || []);
      setFilterButtons(filterButtons.data.filterButtons || []);
      setIsLoading(false);
    } catch (fetchError) {
      console.error('Initial Data Fetch Error:', fetchError);
      setError('Failed to fetch initial data');
      setIsLoading(false);
    }
  }, []);
  useEffect(() => {
    if (auth.currentUser) {
      fetchInitialData();
    }
  }, [auth.currentUser, fetchInitialData]);

  // Auto-select required panel items when roles change or when leftSidePanelItems are loaded
  useEffect(() => {
    if (leftSidePanelItems.length > 0 && (formData.isAdmin || formData.isManager || formData.isStaff)) {
      const requiredItems = getRequiredPanelItems(formData.isAdmin, formData.isManager, formData.isStaff);
      const currentItems = formData.leftSidePanelItems || [];

      // Check if any required items are missing
      const missingRequiredItems = requiredItems.filter(item => !currentItems.includes(item));

      if (missingRequiredItems.length > 0) {
        setFormData(prev => ({
          ...prev,
          leftSidePanelItems: [...currentItems, ...missingRequiredItems]
        }));
      }
    }
  }, [formData.isAdmin, formData.isManager, formData.isStaff, leftSidePanelItems]);

  // Helper function to auto-select required panel items based on roles
  const getRequiredPanelItems = (isAdmin: boolean, isManager: boolean, isStaff: boolean) => {
    const requiredItems: string[] = [];

    // Find Dashboard and Team Management IDs
    const dashboardItem = leftSidePanelItems.find(item => item.name === 'Dashboard');
    const teamManagementItem = leftSidePanelItems.find(item => item.name === 'Team Management');
    const settingsItem = leftSidePanelItems.find(item => item.name === 'Settings');

    // If any role is selected, Dashboard should be auto-selected
    if ((isAdmin || isManager || isStaff) && dashboardItem) {
      requiredItems.push(dashboardItem.id);
    }

    // If only admin is selected, both Dashboard and Team Management should be auto-selected
    if (isAdmin && teamManagementItem) {
      requiredItems.push(teamManagementItem.id);
    }

    // If admin is selected, Settings should be auto-selected
    if (isAdmin && settingsItem) {
      requiredItems.push(settingsItem.id);
    }

    return requiredItems;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'name') {
      const formattedName = value.toUpperCase();
      setFormData(prev => ({ ...prev, [name]: formattedName }));
    } else if (['isAdmin', 'isManager', 'isStaff'].includes(name)) {
      const isYes = value === 'yes';
      setFormData(prev => {
        let updatedFormData = {
          ...prev,
          [name]: isYes,
          ...(name === 'isStaff' && !isYes ? { managerId: '' } : {})
        };

        // Get the updated role values
        const updatedIsAdmin = name === 'isAdmin' ? isYes : prev.isAdmin;
        const updatedIsManager = name === 'isManager' ? isYes : prev.isManager;
        const updatedIsStaff = name === 'isStaff' ? isYes : prev.isStaff;

        // Get required panel items based on new role selection
        const requiredItems = getRequiredPanelItems(updatedIsAdmin, updatedIsManager, updatedIsStaff);

        // Get current panel items
        const currentItems = prev.leftSidePanelItems || [];

        // Merge required items with current items (avoiding duplicates)
        const uniqueItems = new Set([...currentItems, ...requiredItems]);
        const mergedItems = Array.from(uniqueItems);

        // If changing isAdmin from true to false, remove restricted items
        if (name === 'isAdmin' && !isYes && prev.isAdmin) {
          const restrictedItems = ['Team Management', 'Settings', 'Migration Beta', 'Audit Logs', 'Integrations'];

          // Filter out restricted items by finding their IDs, but keep Dashboard if any role is still selected
          const filteredItems = mergedItems.filter(itemId => {
            const item = leftSidePanelItems.find(panelItem => panelItem.id === itemId);
            if (!item) return true;

            // Keep Dashboard if any role is selected
            if (item.name === 'Dashboard' && (updatedIsManager || updatedIsStaff)) {
              return true;
            }

            return !restrictedItems.includes(item.name);
          });

          updatedFormData.leftSidePanelItems = filteredItems;
        } else {
          updatedFormData.leftSidePanelItems = mergedItems;
        }

        return updatedFormData;
      });
    } else if (name === 'callAttemptTarget') {
      // Handle number input specifically
      setFormData(prev => ({ ...prev, [name]: value ? parseInt(value) : 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'name') {
      const formattedName = value.toUpperCase();
      setSubFormData(prev => ({ ...prev, [name]: formattedName }));
    } else if (name === 'username') {
      // Only allow alphanumeric characters in the username field
      const sanitizedValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setSubFormData(prev => ({
        ...prev,
        [name]: sanitizedValue.toLowerCase()
      }));
    } else {
      setSubFormData(prev => ({ ...prev, [name]: value }));
    }
  }

  const handleCredentialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'email') {
      setUpdatedEmail(value);
    } else if (name === 'password') {
      setUpdatedPassword(value);
    }
  }

  const handlePipelineSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedPipeline = e.target.value;

    setFormData(prev => {
      const currentPipelineIds = prev.pipelineIds || [];

      // Check if pipeline is already selected (prevent duplication)
      if (currentPipelineIds.includes(selectedPipeline)) {
        // toast message for duplicate selection
        showToastMessage('Pipeline already selected', true);
        return prev; // Return unchanged state
      }


      return {
        ...prev,
        pipelineIds: [...currentPipelineIds, selectedPipeline]
      };
    });
  }

  const handleRemovePipeline = (pipelineId: string) => {
    setFormData(prev => ({
      ...prev,
      pipelineIds: (prev.pipelineIds ?? []).filter(id => id !== pipelineId)
    }));
  }

  const handleManagerIdsSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedManagerId = e.target.value;

    setFormData(prev => {
      const currentManagerIds = prev.managerIds || [];

      // Check if manager is already selected (prevent duplication)
      if (currentManagerIds.includes(selectedManagerId)) {
        // toast message for duplicate selection
        showToastMessage('Manager already selected', true);
        return prev; // Return unchanged state
      }

      return {
        ...prev,
        managerIds: [...currentManagerIds, selectedManagerId]
      };
    });
  }

  const handleRemoveManagerId = (managerId: string) => {
    setFormData(prev => ({
      ...prev,
      managerIds: (prev.managerIds ?? []).filter(id => id !== managerId)
    }));
  }

  const handleFilterButtonSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedButtonId = e.target.value;

    setFormData(prev => {
      const currentFilterButtons = prev.filterButtons || [];

      // Check if button is already selected (prevent duplication)
      if (currentFilterButtons.includes(selectedButtonId)) {
        // toast message for duplicate selection
        showToastMessage('Filter button already selected', true);
        return prev; // Return unchanged state
      }

      return {
        ...prev,
        filterButtons: [...currentFilterButtons, selectedButtonId]
      };
    });
  }

  const handleRemoveFilterButton = (buttonId: string) => {
    setFormData(prev => ({
      ...prev,
      filterButtons: (prev.filterButtons ?? []).filter(id => id !== buttonId)
    }));
  }

  const fetchPhotoUrl = async (photoName: string) => {
    try {
      if (!companyId || !formData.id || !photoName) return;
      const storageRef = ref(storage, `/companies/staff_profiles/${companyId}/${formData.id}/${photoName}`);
      const url = await getDownloadURL(storageRef);
      setPhotoUrl(url);
    } catch (error) {
      console.error("Error fetching photo URL:", error);
      setPhotoUrl('');
    }
  };

  useEffect(() => {
    if (formData.photo) {
      fetchPhotoUrl(formData.photo);
    } else {
      setPhotoUrl('');
    }
  }, [formData.photo, companyId]);

  const userId = currentUser?.uid

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !companyId || !formData.id) return;

    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      showToastMessage("Only JPG, JPEG, and PNG files are allowed", true);
      return;
    }

    // Set pending upload
    setPendingPhotoChanges({
      action: 'upload',
      file,
      fileName: file.name
    });

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result as string);
    };
    reader.readAsDataURL(file);

    setPhotoName(file.name);
    const now = new Date();
    const formattedDate = now.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
    const formattedTime = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    setUploadTime(`${formattedDate}, ${formattedTime} · Just now`);
  };

  const handleDeletePhoto = async () => {
    if (!formData.photo && !pendingPhotoChanges.fileName) {
      // No photo to delete
      return;
    }

    setPendingPhotoChanges({
      action: 'delete'
    });

    // Clear preview
    setPreviewImage(null);
    setPhotoName('No file selected');
    setUploadTime('');
  };

  const handleAddStaffClick = () => {
    resetFormData(); // Clear any existing data
    setAddPopupOpen(true);
  };

  const handlePhotoClick = () => {
    try {
      if (photoUrl) {
        window.open(photoUrl, '_blank');
        setToastMessage('Photo opened successfully');
        setIsErrorToast(false);
        setShowToast(true);
      } else if (!isLoading) {
        fileInputRef.current?.click();
        setToastMessage('Select a photo to upload');
        setIsErrorToast(true);
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error handling photo click:', error);
      setToastMessage('Error opening or selecting photo');
      setIsErrorToast(true);
      setShowToast(true);
    }
  };

  const getManagers = useMemo(() => {
    return userData.filter(user => user.isManager && user.isActive);
  }, [userData]);

  const resetFormData = () => {
    setFormData({
      id: '',
      name: '',
      email: '',
      phone: '',
      staffRoleId: '',
      departmentId: '',
      password: '',
      isAdmin: false,
      isManager: false,
      isStaff: false,
      managerId: '',
      managerIds: [],
      callAttemptTarget: 0,
      pipelineIds: [],
      filterButtons: [],
      leftSidePanelItems: [],
      photo: '', // <-- add this
    });

    setSubFormData({
      id: '',
      name: '',
      username: '',
      password: '',
      email: '',
      phone: '',
      permissionId: '',
    });

    setAddPopupOpen(false);
    setSubAddPopupOpen(false);
    setSubEditPopupOpen(false);
    setSubEmailResetPopupOpen(false);
    setUpdatedEmail('');
    setUpdatedPassword('');
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const requiredFields: (keyof FormData)[] = ['name', 'email', 'departmentId'];
      if (formData.isStaff) {
        requiredFields.push('managerIds');
      }

      // Validate that at least one role is selected
      if (!formData.isAdmin && !formData.isManager && !formData.isStaff) {
        showToastMessage('Please select at least one role (Admin, Manager, or Staff)');
        return;
      }

      const missingFields = requiredFields.filter(field => {
        const value = formData[field];
        return !value || (typeof value === 'string' && value.trim() === '') ||
          (field === 'managerIds' && formData.isStaff && formData.managerIds.length === 0);
      });

      if (missingFields.length > 0) {
        showToastMessage(`Please fill in all required fields: ${missingFields.join(', ')}`);
        return;
      }

      const licenceData = await checkLicensesLimit(db, auth.currentUser);
      const data = licenceData.data as {
        licencesLimit: number,
        totalUsersCount: number,
        canAddMoreUsers: boolean
      };

      if (!data.canAddMoreUsers) {
        showToastMessage(`Cannot add more users. You have reached the maximum limit of ${data.licencesLimit} users. Please upgrade your plan.`);
        setIsSubmitting(false);
        return;
      }

      // Ensure leftSidePanelItems is properly formatted
      const leftSidePanelItems = Array.isArray(formData.leftSidePanelItems)
        ? formData.leftSidePanelItems.filter(id => id) // Remove any empty/null values
        : [];

      const userData = {
        name: formData.name.trim().toUpperCase(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        companyId: companyId || '',
        staffRoleId: formData.staffRoleId,
        departmentId: formData.departmentId,
        password: formData.password,
        isAdmin: formData.isAdmin,
        isManager: formData.isManager,
        isStaff: formData.isStaff,
        managerId: formData.isStaff ? formData.managerId : null,
        managerIds: formData.isStaff ? formData.managerIds : [],
        callAttemptTarget: formData.callAttemptTarget || 0,
        pipelineIds: formData.pipelineIds || [],
        filterButtons: formData.filterButtons || [],
        leftSidePanelItems: leftSidePanelItems, // Use the properly formatted array
        isActive: true,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };

      console.log('Submitting user data:', userData); // Debug log

      const result = await createUserFunction(userData) as UserCreationResult;

      if (result.data.isActive === false) {
        showToastMessage('User created but marked as inactive. Total number of users exceeds the limit. Please upgrade your plan.');
      } else {
        showToastMessage('User created successfully!');
      }

      resetFormData();
      setAddPopupOpen(false);
      await fetchCompanyAndUsers();
      await fetchInitialData();
    } catch (error: any) {
      console.error('User creation error:', error);
      if (error.code === 'resource-exhausted') {
        showToastMessage('Cannot create user. Maximum licence limit reached. Please upgrade your plan.');
      } else if (error.code === 'already-exists') {
        showToastMessage('A user with this email already exists');
      } else if (error.message) {
        showToastMessage(error.message);
      } else {
        showToastMessage('Failed to create user. Please check your information and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const requiredFields: (keyof SubFormData)[] = ['name', 'username', 'password', 'permissionId'];

      const missingFields = requiredFields.filter(field => {
        const value = subFormData[field];
        return !value || (typeof value === 'string' && value.trim() === '');
      });

      if (missingFields.length > 0) {
        showToastMessage(`Please fill in all required fields: ${missingFields.join(', ')}`);
        return;
      }

      const userData = {
        name: subFormData.name.trim().toUpperCase(),
        username: subFormData.username.trim(),
        password: subFormData.password,
        email: subFormData.email?.trim(), // Safe optional chaining
        phone: subFormData.phone.trim(),
        permissionId: subFormData.permissionId,
        companyId: companyId || '',
      };

      const result = await createSubAgentFunction(userData) as UserCreationResult;

      if (result.data.isActive === false) {
        showToastMessage('User created but marked as inactive. Total number of users exceeds the limit. Please upgrade your plan.');
      } else {
        showToastMessage(result.data.message || 'Affiliate Partner created successfully!');
      }

      resetFormData();
      setSubAddPopupOpen(false);
      await fetchInitialData();
    } catch (error: any) {
      console.error('User creation error:', error);
      if (error.code === 'resource-exhausted') {
        showToastMessage('Cannot create user. Maximum licence limit reached. Please upgrade your plan.');
      } else if (error.code === 'already-exists') {
        showToastMessage('A user with this email already exists');
      } else if (error.message) {
        showToastMessage(error.message);
      } else {
        showToastMessage('Failed to create user. Please check your information and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  const openEditPopup = async (user: UserData) => {
    setFormData({
      id: user.id,
      name: user.name || '',
      email: user.email,
      phone: user.phone,
      staffRoleId: user.staffRoleId || '',
      departmentId: user.departmentId || '',
      password: '',
      isAdmin: user.isAdmin || false,
      isManager: user.isManager || false,
      isStaff: user.isStaff || false,
      managerId: user.managerId || '',
      managerIds: user.managerIds || [],
      callAttemptTarget: user.callAttemptTarget,
      pipelineIds: user.pipelineIds || [],
      filterButtons: user.filterButtons || [],
      leftSidePanelItems: user.leftSidePanelItems || [], // Add this line
      photo: user.photo || '',
    });

    // Reset photo states
    setPendingPhotoChanges({ action: null });
    setPreviewImage(null);
    setPhotoName('No file selected');
    setUploadTime('');

    // Load existing photo if available
    if (user.photo && companyId) {
      try {
        const storageRef = ref(storage, `/companies/staff_profiles/${companyId}/${user.id}/${user.photo}`);
        const url = await getDownloadURL(storageRef);
        setPreviewImage(url);
        setPhotoName(user.photo.split('_').slice(1).join('_')); // Extract original filename

        // Get metadata for upload time
        const metadata = await getMetadata(storageRef);
        if (metadata.timeCreated) {
          const uploadDate = new Date(metadata.timeCreated);
          const formattedDate = uploadDate.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
          const formattedTime = uploadDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
          setUploadTime(`${formattedDate}, ${formattedTime}`);
        }
      } catch (error) {
        console.error("Error loading existing photo:", error);
        setPhotoName('No file selected');
      }
    }

    setEditPopupOpen(true);
  };

  const openSubEditPopup = (user: SubUserData) => {
    console.log('Opening Sub Edit Popup:', user);

    setSubFormData({
      id: user.id,
      name: user.name || '',
      username: user.username || '',
      password: '',
      email: user.email,
      phone: user.phone,
      permissionId: user.permissionId || '',
    });
    setSubEditPopupOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isUpdating) return;

    setIsUpdating(true);

    try {
      // Validate form data
      const validationError = validateFormData(formData);
      if (validationError) {
        showToastMessage(validationError, true);
        return;
      }

      let newPhotoName = formData.photo;

      // Handle pending photo changes
      if (pendingPhotoChanges.action === 'upload' && pendingPhotoChanges.file) {
        // Delete existing photo if any
        if (formData.photo) {
          const storageRef = ref(storage, `/companies/staff_profiles/${companyId}/${formData.id}/${formData.photo}`);
          await deleteObject(storageRef);
        }

        // Upload new photo
        const fileName = `${uuidv4()}_${pendingPhotoChanges.file.name}`;
        const storageRef = ref(storage, `/companies/staff_profiles/${companyId}/${formData.id}/${fileName}`);
        await uploadBytes(storageRef, pendingPhotoChanges.file);
        newPhotoName = fileName;
      } else if (pendingPhotoChanges.action === 'delete' && formData.photo) {
        // Delete photo
        const storageRef = ref(storage, `/companies/staff_profiles/${companyId}/${formData.id}/${formData.photo}`);
        await deleteObject(storageRef);
        newPhotoName = '';
      }

      // Create the updated data object
      const updatedData = {
        ...normalizeUserData(formData),
        photo: newPhotoName,
        leftSidePanelItems: formData.leftSidePanelItems || [],
      };

      // Update user document
      const docRef = doc(db, 'users', formData.id);
      await updateDoc(docRef, updatedData);

      // Reset pending changes
      setPendingPhotoChanges({ action: null });

      // Show success message and close popup
      showToastMessage('Staff updated successfully!', false);
      setEditPopupOpen(false);
      await fetchInitialData();
      await fetchCompanyAndUsers();
    } catch (error) {
      console.error("Error updating staff:", error);
      showToastMessage('An error occurred while updating staff. Please try again.', true);
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper functions for better organization and reusability

  const validateFormData = (formData: any): string | null => {
    const requiredFields: (keyof typeof formData)[] = ['name', 'email', 'departmentId'];

    if (formData.isStaff) {
      requiredFields.push('managerIds');
    }

    const missingFields = requiredFields.filter(field => {
      const value = formData[field];
      if (typeof value === 'boolean') return false;
      if (typeof value === 'string') return !value?.trim();
      return !value;
    });

    return missingFields.length > 0
      ? `Please fill in all required fields: ${missingFields.join(', ')}`
      : null;
  };

  const normalizeUserData = (formData: any) => ({
    name: formData.name?.trim().toUpperCase(),
    email: formData.email?.trim().toLowerCase(), // Normalize email case
    phone: formData.phone?.trim(),
    staffRoleId: formData.staffRoleId,
    departmentId: formData.departmentId,
    isAdmin: Boolean(formData.isAdmin),
    isManager: Boolean(formData.isManager),
    isStaff: Boolean(formData.isStaff),
    managerId: formData.isStaff ? formData.managerId : null,
    managerIds: formData.isStaff ? formData.managerIds : [],
    callAttemptTarget: Number(formData.callAttemptTarget) || 0,
    pipelineIds: Array.isArray(formData.pipelineIds) ? formData.pipelineIds : [],
    filterButtons: Array.isArray(formData.filterButtons) ? formData.filterButtons : [],
    photo: profilePhoto
  });

  const detectUserDataChanges = (original: any, updated: any) => {
    const changes: Record<string, { before: any; after: any }> = {};
    let hasChanges = false;

    // Helper function to convert undefined to null for Firestore compatibility
    const sanitizeForFirestore = (value: any): any => {
      if (value === undefined) return null;
      if (Array.isArray(value)) return value.map(sanitizeForFirestore);
      if (value && typeof value === 'object') {
        const sanitized: any = {};
        Object.keys(value).forEach(key => {
          sanitized[key] = sanitizeForFirestore(value[key]);
        });
        return sanitized;
      }
      return value;
    };

    // More robust comparison function
    const isEqual = (a: any, b: any): boolean => {
      // Convert undefined to null for comparison
      const normalizedA = a === undefined ? null : a;
      const normalizedB = b === undefined ? null : b;

      // Handle null cases
      if (normalizedA === null && normalizedB === null) return true;
      if (normalizedA === null || normalizedB === null) return false;

      // Handle arrays
      if (Array.isArray(normalizedA) && Array.isArray(normalizedB)) {
        if (normalizedA.length !== normalizedB.length) return false;
        return normalizedA.every((item, index) => isEqual(item, normalizedB[index]));
      }

      // Handle objects
      if (typeof normalizedA === 'object' && typeof normalizedB === 'object') {
        const keysA = Object.keys(normalizedA);
        const keysB = Object.keys(normalizedB);
        if (keysA.length !== keysB.length) return false;
        return keysA.every(key => isEqual(normalizedA[key], normalizedB[key]));
      }

      // Handle primitives (including type coercion for numbers)
      return normalizedA === normalizedB;
    };

    // Check each field in updated data
    Object.entries(updated).forEach(([key, newValue]) => {
      const originalValue = original[key];

      if (!isEqual(originalValue, newValue)) {
        changes[key] = {
          before: sanitizeForFirestore(originalValue),
          after: sanitizeForFirestore(newValue)
        };
        hasChanges = true;
      }
    });

    return { hasChanges, changes };
  };

  const fetchOriginalUserData = async (userId: string) => {
    const userDocRef = doc(db, 'users', userId);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      throw new Error('User document not found');
    }

    return userDocSnap.data();
  };

  const updateUserWithHistory = async (userId: string, updatedData: any, changes: any) => {
    const userDocRef = doc(db, 'users', userId);

    // Use a batch for atomic operations
    const batch = writeBatch(db);

    // Update user document
    batch.update(userDocRef, {
      ...updatedData,
      userDataUpdatedAt: serverTimestamp(),
    });

    // Add history record
    const historyDocRef = doc(collection(userDocRef, 'history'));
    batch.set(historyDocRef, {
      changes,
      companyId: companyId,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || 'unknown',
    });

    await batch.commit();
  };

  const handleUpdateSuccess = async () => {
    resetFormData();
    setEditPopupOpen(false);
    await fetchCompanyAndUsers();
    showToastMessage('User updated successfully!', false);

    console.log('User updated:', {
      userId: formData.id,
      timestamp: new Date().toISOString()
    });
  };

  const handleUpdateError = (error: any) => {
    console.error("Error updating user:", error);
    const errorMessage = error.message || 'An error occurred while updating the user. Please try again.';
    showToastMessage(errorMessage, true);
  };

  const handleSubEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      const requiredFields: (keyof typeof subFormData)[] = [
        'name', 'username', 'permissionId'
      ];
      const missingFields = requiredFields.filter(field => {
        const value = subFormData[field];
        if (typeof value === 'string') {
          return !value || value.trim() === '';
        }
        return !value;
      });
      if (missingFields.length > 0) {
        showToastMessage(`Please fill in all required fields: ${missingFields.join(', ')}`, true);
        return;
      }

      const result = await updateSubAgent(db, auth.currentUser, subFormData.id, {
        name: subFormData.name.trim().toUpperCase(),
        username: subFormData.username.trim(),
        email: subFormData.email?.trim(), // Safe optional chaining
        phone: subFormData.phone.trim(),
        permissionId: subFormData.permissionId,
      });

      if (result) {
        resetFormData();
        console.log('Edit Submission:', {
          subFormData,
          timestamp: new Date().toISOString()
        });
        setSubEditPopupOpen(false);
        await fetchInitialData();
        showToastMessage(result?.message, false);
      } else {
        showToastMessage('An error occurred while updating the affiliate partner. Please try again.', true);
      }
    }
    catch (error) {
      console.error("Error updating affiliate partner:", error);
      showToastMessage('An error occurred while updating the affiliate partner. Please try again.', true);
    }
    finally {
      setIsUpdating(false);
    }
  };

  const openEmailEditPopup = (user: UserData) => {
    setFormData({
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone,
      staffRoleId: user.staffRoleId || '',
      departmentId: user.departmentId || '',
      password: '',
      isAdmin: user.isAdmin || false,
      isManager: user.isManager || false,
      isStaff: !user.isAdmin && !user.isManager,
      managerId: user.managerId || '',
      managerIds: user.managerIds || [],
      photo: user.image || '',
      callAttemptTarget: user.callAttemptTarget || 0,
      pipelineIds: user.pipelineIds || [],
    });
    setEmailResetPopupOpen(true);
  };

  const handleEmailEditSubmit = async (e: React.FormEvent) => {
    console.log('Email Edit Submit:', formData, updatedEmail);
    setIsDisabled(true);
    e.preventDefault();
    try {
      const updateUserEmail = httpsCallable(functions, 'updateEmail');
      const result = await updateUserEmail({
        email: formData.email,
        updatedEmail: updatedEmail,
        oldPassword: formData.password
      });
      console.log('Email Update Result:', result);
      showToastMessage('Email updated successfully!', false);
      setEmailResetPopupOpen(false);
      setUpdatedEmail('');
      setIsDisabled(false)
      await fetchCompanyAndUsers();
    }
    catch (error) {
      console.error("Error updating email:", error);
      showToastMessage('An error occurred while updating email. Please try again.', true);
      setIsDisabled(false);
    }
  };

  const openPasswordEditPopup = (user: UserData) => {
    setFormData({
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone,
      staffRoleId: user.staffRoleId || '',
      departmentId: user.departmentId || '',
      password: '',
      isAdmin: user.isAdmin || false,
      isManager: user.isManager || false,
      isStaff: !user.isAdmin && !user.isManager,
      managerId: user.managerId || '',
      managerIds: user.managerIds || [],
      callAttemptTarget: user.callAttemptTarget || 0,
      pipelineIds: user.pipelineIds || [],
      photo: user.image || '', // <-- add this
    });
    setPasswordResetPopupOpen(true);
  };

  const openSubPasswordEditPopup = (user: SubUserData) => {
    setSubFormData({
      id: user.id,
      name: user.name,
      username: user.username,
      password: '',
      email: user.email,
      phone: user.phone,
      permissionId: user.permissionId || '',
    });
    setSubPasswordResetPopupOpen(true);
  };

  const openSubEmailEditPopup = (user: SubUserData) => {
    setSubFormData({
      id: user.id,
      name: user.name,
      username: user.username,
      password: '',
      email: user.email,
      phone: user.phone,
      permissionId: user.permissionId || '',
    });
    setSubEmailResetPopupOpen(true);
  };

  const autoGeneratePassword = () => {
    const random = uuidv4()
    setUpdatedPassword(random.split('-')[0] + random.split('-')[1])
  }

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(updatedPassword);
      setCopySuccess("Copied to clipboard!");
      setFadeOut(false); // Reset fadeOut before setting the success message
      setTimeout(() => {
        setFadeOut(true); // Trigger fade-out effect
        setTimeout(() => {
          setCopySuccess(""); // Hide the message after fade
        }, 1000); // After 1 second (fade duration), hide the message
      }, 3000); // Show message for 3 seconds
    } catch (err) {
      setCopySuccess("Failed to copy!");
    }
  };


  const handlePasswordEditSubmit = async (e: React.FormEvent) => {
    console.log('Password Edit Submit:', formData, updatedPassword);
    setIsDisabled(true);
    e.preventDefault();
    try {
      const updateUserPassword = httpsCallable(functions, 'updatePassword');
      const result = await updateUserPassword({
        email: formData.email,
        newPassword: updatedPassword
      });
      console.log('Password Update Result:', result);
      showToastMessage('Password updated successfully!', false);
      setPasswordResetPopupOpen(false);
      setUpdatedPassword('');
      setIsDisabled(false);
      await fetchCompanyAndUsers();
    }
    catch (error) {
      console.error("Error updating password:", error);
      showToastMessage('An error occurred while updating password. Please try again.', true);
      setIsDisabled(false);
    }
  };

  const handleSubPasswordEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!updatedPassword) {
      showToastMessage('Please enter a password', true);
      return;
    }

    setIsDisabled(true);
    try {
      const updateSubAgentPasswordFunction = httpsCallable(functions, 'updateSubAgentPasswordWithAdminAuth');
      const result = await updateSubAgentPasswordFunction({
        id: subFormData.id,
        password: updatedPassword,
        companyId: companyId
      });

      showToastMessage('Password updated successfully');
      setSubPasswordResetPopupOpen(false);
      setUpdatedPassword('');
      resetFormData();
    } catch (error) {
      console.error('Error updating password:', error);
      showToastMessage('Error updating password', true);
    } finally {
      setIsDisabled(false);
    }
  };

  const handleSubEmailEditSubmit = async (e: React.FormEvent) => {
    console.log('Sub Email Edit Submit:', subFormData, updatedEmail);
    setIsDisabled(true);
    e.preventDefault();
    
    // Validate required fields
    if (!updatedEmail || !subFormData.password) {
      showToastMessage('Please fill in all required fields', true);
      setIsDisabled(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(updatedEmail)) {
      showToastMessage('Please enter a valid email address', true);
      setIsDisabled(false);
      return;
    }

    try {
      // Try using the cloud function with admin auth pattern first
      const updateSubAgentEmailFunction = httpsCallable(functions, 'updateSubAgentEmailWithAdminAuth');
      const result = await updateSubAgentEmailFunction({
        id: subFormData.id,
        email: subFormData.email,
        updatedEmail: updatedEmail,
        oldPassword: subFormData.password,
        companyId: companyId
      });
      
      console.log('Sub Email Update Result:', result);
      showToastMessage('Email updated successfully!', false);
      setSubEmailResetPopupOpen(false);
      setUpdatedEmail('');
      setIsDisabled(false);
      await fetchInitialData();
    }
    catch (error: any) {
      console.error("Error updating email:", error);
      
      // If the cloud function doesn't exist, fall back to direct update
      if (error.code === 'not-found' || error.message?.includes('Function not found')) {
        try {
          // Fallback: Use the existing updateSubAgent function
          const result = await updateSubAgent(db, auth.currentUser, subFormData.id, {
            name: subFormData.name?.trim().toUpperCase(),
            username: subFormData.username?.trim(),
            email: updatedEmail.trim(),
            phone: subFormData.phone?.trim(),
            permissionId: subFormData.permissionId,
          });

          if (result) {
            showToastMessage('Email updated successfully!', false);
            setSubEmailResetPopupOpen(false);
            setUpdatedEmail('');
            await fetchInitialData();
          } else {
            showToastMessage('Failed to update email. Please try again.', true);
          }
        } catch (fallbackError) {
          console.error("Fallback update error:", fallbackError);
          showToastMessage('An error occurred while updating email. Please try again.', true);
        }
      } else {
        showToastMessage('An error occurred while updating email. Please try again.', true);
      }
      setIsDisabled(false);
    }
  };

  const fetchLeftSidePanelItems = async (db: any, currentUser: any) => {
    if (!currentUser) throw new Error("User not authenticated");

    const companyDoc = await getDoc(doc(db, "users", currentUser.uid));
    if (!companyDoc.exists()) throw new Error("Company not found");

    // const companyId = companyDoc.data().companyId;
    const panelItemsRef = collection(db, `leftSidePanelItems`);
    const panelItemsSnapshot = await getDocs(panelItemsRef);

    const panelItems = panelItemsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as LeftSidePanelItem[];

    return { data: { panelItems } };
  };

  const fetchCompanyAndUsers = async () => {
    try {
      // console.log('Fetching Company and Users - Started', new Date().toISOString());
      try {
        const staffRolesResult = await fetchStaffRoles(db, auth.currentUser);
        const rolesData = staffRolesResult.data as FetchStaffRolesResult;
        // console.log('Staff Roles Fetched', {
        //   rolesCount: Object.keys(rolesData.roles || {}).length});
        setStaffRoles(rolesData.roles);
      } catch (rolesError) {
        console.error('Staff Roles Fetch Error:', rolesError);
        console.error('Detailed Error:', rolesError);
        alert('Failed to fetch staff roles');
        return;
      }
      try {
        const companyUsersResult = await fetchCompanyUsers(db, auth.currentUser);
        const usersData = companyUsersResult.data as FetchCompanyUsersResult;
        // console.log('Company Users Fetched', {
        //   usersCount: usersData.users?.length || 0,
        //   companyId: usersData.companyId});
        setCompanyId(usersData.companyId);
        setUserData(usersData.users);
      }
      catch (usersError) {
        console.error('Company Users Fetch Error:', usersError);
        console.error('Detailed Error:', usersError);
        alert('Failed to fetch company users');
        return;
      }
      // console.log('Fetch Company and Users - Completed Successfully', new Date().toISOString());
    }
    catch (error) {
      console.error('DETAILED Fetch Company Users Frontend Error:', {
        errorName: error instanceof Error ? error.name : 'Unknown Error',
        errorMessage: error instanceof Error ? error.message : 'No error message',
        errorStack: error instanceof Error ? error.stack : 'No stack trace',
        timestamp: new Date().toISOString()
      });
      alert('An unexpected error occurred while fetching data');
    }
  };

  const handleDeleteUser = async (user: UserData) => {
    try {
      // const confirmDelete = window.confirm(`Are you sure you want to ${user.isActive ? 'deactivate' : 'reactivate'} ${user.name}?`);
      // if (!confirmDelete) return;
      const currentUser = auth.currentUser;
      if (!currentUser) {
        showToastMessage('You must be logged in to perform this action', true);
        return;
      }
      const userDocRef = doc(db, 'users', user.id);
      await updateDoc(userDocRef, { isActive: !user.isActive });
      await fetchCompanyAndUsers();
      showToastMessage(`User ${user.name}  has been ${user.isActive ? 'deactivated' : 'reactivated'} successfully.`, false);
    } catch (error) {
      console.error("Error updating user status:", error);
      showToastMessage('An error occurred while updating user status. Please try again.', true);
    }
  };

  const handleDeleteSubUser = async (user: SubUserData) => {
    console.log('Deleting Sub User:', user);
    console.log('Sub User ID:', user.id);

    try {
      // const confirmDelete = window.confirm(`Are you sure you want to ${user.isActive ? 'deactivate' : 'reactivate'} ${user.name}?`);
      // if (!confirmDelete) return;
      const currentUser = auth.currentUser;
      if (!currentUser) {
        showToastMessage('You must be logged in to perform this action', true);
        return;
      }
      const userDocRef = doc(db, `companies/${companyId}/subAgents/${user.id}`);
      await updateDoc(userDocRef, { isActive: !user.isActive });
      await fetchInitialData();
      showToastMessage(`Affiliate Partner ${user.name} has been ${user.isActive ? 'deactivated' : 'reactivated'} successfully.`, false);
    } catch (error) {
      console.error("Error updating affiliate partner status:", error);
      showToastMessage('An error occurred while updating affiliate partner status. Please try again.', true);
    }
  }

  const groupStaffByDepartment = useCallback((users: UserData[], departments: { id: string, name: string }[]) => {
    const departmentMap: Record<string, Department> = {};

    // Initialize departments
    departments?.forEach(dept => {
      departmentMap[dept.id] = {
        id: dept.id,
        name: dept.name,
        staff: []
      };
    });

    // Add an "Unassigned" department for staff without a department
    departmentMap['unassigned'] = {
      id: 'unassigned',
      name: 'Unassigned',
      staff: []
    };

    // Group staff by department
    users?.forEach(user => {
      const deptId = user.departmentId || 'unassigned';
      if (departmentMap[deptId]) {
        departmentMap[deptId].staff.push(user);
      } else {
        departmentMap['unassigned'].staff.push(user);
      }
    });

    // Exclude departments with no staff, including 'Unassigned' if empty
    const nonEmptyDepartments = Object.values(departmentMap).filter(dept => dept.staff.length > 0);

    return nonEmptyDepartments;
  }, []);

  const toggleDepartment = (departmentId: string) => {
    setExpandedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(departmentId)) {
        newSet.delete(departmentId);
      } else {
        newSet.add(departmentId);
      }
      return newSet;
    });
  };

  // const fetchDepartments = async (db: any, currentUser: any) => {
  //   if (!currentUser) throw new Error("User not authenticated");

  //   const companyDoc = await getDoc(doc(db, "users", currentUser.uid));
  //   if (!companyDoc.exists()) throw new Error("Company not found");

  //   const companyId = companyDoc.data().companyId;
  //   const departmentsRef = collection(db, `companies/${companyId}/departments`);
  //   const departmentsSnapshot = await getDocs(departmentsRef);

  //   const departments = departmentsSnapshot.docs.map(doc => ({
  //     id: doc.id,
  //     ...doc.data()
  //   })) as DepartmentData[];

  //   return { data: { departments } };
  // };

  const isCollapsed = useAppSelector((state) => state.sidebar.isCollapsed);

  // Position cursor before the postfix
  useEffect(() => {
    if (inputRef.current && subFormData.username.endsWith(loginPostfix)) {
      const pos = Math.max(0, subFormData.username.length - loginPostfix.length);
      inputRef.current.setSelectionRange(pos, pos);
    }
  }, [subFormData.username, loginPostfix]);

  return (
    <div className="min-h-screen bg-zinc-100">
      <SideNavbar />
      <div className="flex-1">
        <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 pb-3 transition-all duration-300`}>
          <div className="bg-white p-6 rounded-3xl shadow-md">
            <div className="flex items-center gap-6 mb-8">
              <div
                className="flex items-center justify-center w-14 h-14 rounded-full"
                style={{ background: 'linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)', }}>
                <img src={`${process.env.PUBLIC_URL}/images/desktop/healthicons-staff.svg`} alt="Staff Icon" />
              </div>
              <h2 className="text-2xl font-medium text-black"
                style={{ fontFamily: 'Montserrat', fontSize: '24px', fontWeight: '500', lineHeight: '29.26px', textAlign: 'left', }}>Staff Management</h2>
              <div className="ml-auto flex items-center">
                <button onClick={() => {
                  resetFormData();
                  setAddPopupOpen(true)
                }}
                  className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2"
                  style={{ background: 'linear-gradient(180deg, #FFA782 0%, #FC583D 100%)', }}>
                  <span style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: '500', lineHeight: '24.38px', }}>  Add Staff </span>
                  <img src={`${process.env.PUBLIC_URL}/images/desktop/staffadd.svg`} alt="Add Staff Icon" className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden bg-white shadow-lg border border-gray-200">
              {isLoading ? (
                <TableSkeleton columns={7} rows={5} />
              ) : (
                <div className="relative">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse table-fixed">
                      <thead>
                        <tr className="bg-gradient-to-r from-[#02385E] to-[#02385E]/90" style={{ height: '48px' }}>
                          <th className="px-4 py-0 h-12 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat w-[5%]">
                            Sl No
                          </th>
                          <th className="px-4 py-0 h-12 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat w-[20%]">
                            Name
                          </th>
                          <th className="px-4 py-0 h-12 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat w-[25%]">
                            Email
                          </th>
                          <th className="px-4 py-0 h-12 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat w-[15%]">
                            Phone
                          </th>
                          <th className="px-4 py-0 h-12 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat w-[15%]">
                            Designation
                          </th>
                          <th className="px-4 py-0 h-12 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat w-[15%]">
                            Last Visit
                          </th>
                          <th className="px-4 py-0 h-12 text-left text-xs font-medium text-white uppercase tracking-wider font-Montserrat w-[25%]">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {departmentsWithStaff.map((department, departmentIndex) => (
                          <React.Fragment key={`dept-${department.id}`}>
                            <tr className="bg-gradient-to-r from-[#10161a] to-[#02385E]">
                              <td colSpan={7} className="px-5 text-white" style={{ height: '48px' }}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <h3 className="text-lg font-semibold font-Montserrat tracking-wide">
                                      {department.name}
                                    </h3>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                    <span className="text-sm font-medium font-Montserrat">
                                      {department.staff.length} {department.staff.length === 1 ? 'staff' : 'staff'}
                                    </span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                            {department.staff.length === 0 && (
                              <tr className="bg-white">
                                <td colSpan={7} className="px-5 py-6 text-center text-sm text-gray-500 font-Montserrat">
                                  No staff members in this department.
                                </td>
                              </tr>
                            )}
                            {department.staff.map((user, index) => (
                              <tr
                                key={user.id}
                                className={`hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}
                              >
                                <td className="px-4 py-4 border-r border-gray-100 text-sm text-neutral-600 font-medium font-Montserrat text-center overflow-hidden text-ellipsis whitespace-nowrap">
                                  {index + 1}
                                </td>
                                <td className="px-4 py-4 border-r border-gray-100 text-sm text-neutral-600 font-medium font-Montserrat overflow-hidden text-ellipsis whitespace-nowrap">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-b from-[#A7C8E7] to-[#00A6D9] text-white flex items-center justify-center text-xs font-semibold">
                                      {((user.name || '').trim().split(/\s+/).map(n => n[0]).join('').slice(0, 2) || 'U').toUpperCase()}
                                    </div>
                                    <span className="truncate">{user.name}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-4 border-r border-gray-100 text-sm text-neutral-600 font-Montserrat overflow-hidden text-ellipsis whitespace-nowrap">
                                  {user.email}
                                </td>
                                <td className="px-4 py-4 border-r border-gray-100 text-sm text-neutral-600 font-Montserrat overflow-hidden text-ellipsis whitespace-nowrap">
                                  {user.phone}
                                </td>
                                <td className="px-4 py-4 border-r border-gray-100 text-sm text-neutral-600 font-Montserrat overflow-hidden text-ellipsis whitespace-nowrap">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {staffRoles[user.staffRoleId || ''] || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-4 py-4 border-r border-gray-100 text-sm text-neutral-600 font-Montserrat overflow-hidden text-ellipsis whitespace-nowrap">
                                  {user.lastAppVisitTimestamp ? (
                                    <div className="flex flex-col">
                                      <span className="font-medium">
                                        {new Date(user.lastAppVisitTimestamp.seconds * 1000).toLocaleDateString()}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {new Date(user.lastAppVisitTimestamp.seconds * 1000).toLocaleTimeString()}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                      Never
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-4 text-sm text-neutral-600 font-Montserrat overflow-hidden text-ellipsis whitespace-nowrap">
                                  <div className="flex items-center gap-3">
                                    <button
                                      title="Edit"
                                      onClick={() => openEditPopup(user)}
                                      className="p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200 group"
                                    >
                                      <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/editicon.svg`}
                                        alt="Edit"
                                        className="w-4 h-4 group-hover:scale-110 transition-transform duration-200"
                                      />
                                    </button>
                                    <label
                                      className="relative inline-flex items-center cursor-pointer group"
                                      title={user.isActive ? 'Deactivate User' : 'Activate User'}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={user.isActive}
                                        onChange={() => setPendingUser(user)}
                                        className="sr-only peer"
                                      />
                                      <div className={`w-11 h-6 rounded-full peer peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 transition-all duration-300 group-hover:shadow-md ${
                                        user.isActive ? 'bg-green-500' : 'bg-red-500'
                                      }`}>
                                        <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow-sm transition-transform duration-300 ${
                                          user.isActive ? 'translate-x-5' : 'translate-x-0'
                                        }`}></div>
                                      </div>
                                    </label>
                                    <button
                                      title="Edit Email"
                                      onClick={() => openEmailEditPopup(user)}
                                      className="p-2 rounded-lg hover:bg-yellow-50 transition-colors duration-200 group"
                                    >
                                      <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/email.svg`}
                                        alt="Email"
                                        className="w-4 h-4 group-hover:scale-110 transition-transform duration-200"
                                      />
                                    </button>
                                    <button
                                      title="Edit Password"
                                      onClick={() => openPasswordEditPopup(user)}
                                      className="p-2 rounded-lg hover:bg-red-50 transition-colors duration-200 group"
                                    >
                                      <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/password.svg`}
                                        alt="Password"
                                        className="w-4 h-4 group-hover:scale-110 transition-transform duration-200"
                                      />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {pendingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-md mx-4 animate-fadeIn">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              {pendingUser.isActive ? 'Deactivate User' : 'Reactivate User'}
            </h2>
            <p className="text-gray-600 text-sm mb-6">
              Are you sure you want to{' '}
              <span className="font-semibold text-gray-800">
                {pendingUser.isActive ? 'deactivate' : 'reactivate'}
              </span>{' '}
              <span className="font-medium text-black">{pendingUser.name}</span>?
            </p>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-md text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                onClick={() => setPendingUser(null)}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm text-white transition ${pendingUser.isActive
                  ? 'bg-red-500 hover:bg-red-600'
                  : 'bg-green-500 hover:bg-green-600'
                  }`}
                onClick={async () => {
                  await handleDeleteUser(pendingUser);
                  setPendingUser(null);
                }}
              >
                {pendingUser.isActive ? 'Deactivate' : 'Reactivate'}
              </button>
            </div>
          </div>
        </div>
      )}


      <div className="flex-1">
        <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} px-8 transition-all duration-300`}>
          <div className="bg-white p-6 rounded-3xl shadow-md">
            <div className="flex items-center gap-6 mb-8">
              <div
                className="flex items-center justify-center w-14 h-14 rounded-full"
                style={{ background: 'linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)', }}>
                <img src={`${process.env.PUBLIC_URL}/images/desktop/healthicons-staff.svg`} alt="Staff Icon" />
              </div>
              <h2 className="text-2xl font-medium text-black"
                style={{ fontFamily: 'Montserrat', fontSize: '24px', fontWeight: '500', lineHeight: '29.26px', textAlign: 'left', }}>Affiliate Partner Management</h2>
              <div className="ml-auto flex items-center">
                <button onClick={() => setSubAddPopupOpen(true)}
                  className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2"
                  style={{ background: 'linear-gradient(180deg, #FFA782 0%, #FC583D 100%)', }}>
                  <span style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: '500', lineHeight: '24.38px', }}>  Add Affiliate Partner </span>
                  <img src={`${process.env.PUBLIC_URL}/images/desktop/staffadd.svg`} alt="Add Staff Icon" className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden bg-white shadow-lg">
              {isLoading ? (
                <TableSkeleton columns={6} rows={5} />
              ) : (
                <div className="overflow-x-auto relative">
                  <table className="min-w-full table-auto border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-[#02385E] to-[#02385E]/90">
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Sl No</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Phone</th>
                        {/* <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Staff Designation</th> */}
                        {/* <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Last App Visit Time</th> */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Action</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {subAgentsData?.map((user, index) => (
                        <tr key={user.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">{index + 1}</td>
                          <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">{user.name}</td>
                          <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">{user.email}</td>
                          <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">{user.phone}</td>
                          {/* <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">{staffRoles[user.staffRoleId || '']}</td> */}
                          {/* <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                        {user.lastAppVisitTimestamp ? 
                          new Date(user.lastAppVisitTimestamp.seconds * 1000).toLocaleString() : 
                          'Never'}
                      </td> */}
                          <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                            <div className="flex gap-4">
                              <img title='Edit' src={`${process.env.PUBLIC_URL}/images/desktop/editicon.svg`} alt="Edit Icon" onClick={() => openSubEditPopup(user)} className="w-5 h-5 cursor-pointer" />
                              <label
                                className="relative inline-flex items-center cursor-pointer group"
                                title={user.isActive ? 'Deactivate User' : 'Activate User'}
                              >
                                <input
                                  type="checkbox"
                                  checked={user.isActive}
                                  onChange={() => setPendingSubUser(user)}
                                  className="sr-only peer"
                                />
                                <div className={`w-11 h-6 rounded-full peer peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 transition-all duration-300 group-hover:shadow-md ${
                                  user.isActive ? 'bg-green-500' : 'bg-red-500'
                                }`}>
                                  <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full shadow-sm transition-transform duration-300 ${
                                    user.isActive ? 'translate-x-5' : 'translate-x-0'
                                  }`}></div>
                                </div>
                              </label>
                              <img title='Edit Email' src={`${process.env.PUBLIC_URL}/images/desktop/email.svg`} alt="Email Icon" onClick={() => openSubEmailEditPopup(user)} className="w-5 h-5 cursor-pointer" />
                              <img title='Edit Password' src={`${process.env.PUBLIC_URL}/images/desktop/password.svg`} alt={"Password Icon"} onClick={() => openSubPasswordEditPopup(user)} className="w-5 h-5 cursor-pointer" />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {pendingSubUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-md mx-4 animate-fadeIn">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              {pendingSubUser.isActive ? 'Deactivate Affiliate Partner' : 'Reactivate Affiliate Partner'}
            </h2>
            <p className="text-gray-600 text-sm mb-6">
              Are you sure you want to{' '}
              <span className="font-semibold text-gray-800">
                {pendingSubUser.isActive ? 'deactivate' : 'reactivate'}
              </span>{' '}
              <span className="font-medium text-black">{pendingSubUser.name}</span>?
            </p>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-md text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                onClick={() => setPendingSubUser(null)}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm text-white transition ${pendingSubUser.isActive
                  ? 'bg-red-500 hover:bg-red-600'
                  : 'bg-green-500 hover:bg-green-600'
                  }`}
                onClick={async () => {
                  await handleDeleteSubUser(pendingSubUser);
                  setPendingSubUser(null);
                }}
              >
                {pendingSubUser.isActive ? 'Deactivate' : 'Reactivate'}
              </button>
            </div>
          </div>
        </div>
      )}



      {isAddPopupOpen && (<div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
        <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative my-8 max-h-[90vh] overflow-y-auto">
          <div className="top-0 left-0 right-0 h-[40px] flex items-center justify-between rounded-tl-[12px] rounded-tr-[12px] p-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                <img src={`${process.env.PUBLIC_URL}/images/desktop/healthicons-staff.svg`} alt="Staff Icon" />
              </div>
              <h2 className="text-xl font-semibold text-black">Add Staff</h2>
            </div>
            <div className="text-right">
              <button onClick={() => { setAddPopupOpen(false); setPasswordShown("password"); }} className="absolute top-4 right-4 font-semibold text-3xl"
                style={{ background: 'linear-gradient(270deg, #A7C8E7 0%, #00A6D9 100%)', WebkitBackgroundClip: 'text', color: 'transparent', }} >
                <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
              </button>
            </div>
          </div>
          <form onSubmit={handleAddSubmit}>
            <div className="grid grid-cols-2 gap-6 mt-8">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter Full Name"
                  required
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                  required
                  autoComplete="new-email"
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ 
                    borderRadius: '24px', 
                    border: '1px solid #3483BE',
                    backgroundColor: 'white !important',
                    color: 'black !important',
                    WebkitBoxShadow: '0 0 0 1000px white inset !important',
                    WebkitTextFillColor: 'black !important'
                  }} />
              </div>
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <input
                  type={passwordShown}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Password"
                  required
                  autoComplete="new-password"
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ 
                    borderRadius: '24px', 
                    border: '1px solid #3483BE',
                    backgroundColor: 'white !important',
                    color: 'black !important',
                    WebkitBoxShadow: '0 0 0 1000px white inset !important',
                    WebkitTextFillColor: 'black !important'
                  }} />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform translate-y-1/5"
                  onClick={() => toogleEyeButton()}
                >
                  <img
                    src={passwordShown === "text" ? `${process.env.PUBLIC_URL}/images/desktop/eye-open.svg` : `${process.env.PUBLIC_URL}/images/desktop/eye-close.svg`}
                    alt={passwordShown === "text" ? "Show Password" : "Hide Password"}
                    className={`w-7 h-7 transition-transform`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+914756688221"
                  required
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE', }} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Staff Designation</label>
                <select
                  name="staffRoleId"
                  value={formData.staffRoleId}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                >
                  <option value="">Select Staff Designation</option>
                  {Object.entries(staffRoles).map(([roleId, roleName]) => (<option key={roleId} value={roleId}>
                    {roleName} </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Staff Department</label>
                <select
                  name="departmentId"
                  value={formData.departmentId}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE', }} >
                  <option value="">Select Staff Department</option>
                  {Array.isArray(departments)
                    && departments.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Is this Employee an Admin?</label>
                <select
                  name="isAdmin"
                  value={formData.isAdmin ? 'yes' : 'no'}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Is this Employee a Manager?</label>
                <select
                  name="isManager"
                  value={formData.isManager ? 'yes' : 'no'}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Is this Employee a Staff?</label>
                <select
                  name="isStaff"
                  value={formData.isStaff ? 'yes' : 'no'}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>

              {formData.isStaff && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Select Managers</label>
                  <select
                    name="managerIds"
                    value={formData.managerIds}
                    onChange={handleManagerIdsSelection}
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  >
                    <option value="">Select Manager</option>
                    {userData
                      .filter(user => (user.isAdmin || user.isManager) && user.isActive)
                      .map((manager) => (
                        <option key={manager.id} value={manager.id}>
                          {manager.name}
                        </option>
                      ))}
                  </select>
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-2">
                      {formData.managerIds?.map((managerId) => {
                        const manager = userData.find(u => u.id === managerId);
                        return (
                          <div
                            key={managerId}
                            className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-800"
                          >
                            {manager ? manager.name : managerId}
                            <button
                              type="button"
                              onClick={() => handleRemoveManagerId(managerId)}
                              className="text-red-500 cursor-pointer"
                            >
                              <img
                                src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                alt="Remove"
                                className="w-4 h-4"
                              />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700">Call Attempt Target</label>
                <input
                  type="number"
                  name="callAttemptTarget"
                  value={formData.callAttemptTarget}
                  onChange={handleInputChange}
                  placeholder="Daily call target"
                  // min="0"
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                />
              </div>

              {/* Pipeline selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Pipeline</label>
                <select
                  name="pipelineId"
                  // value={formData.pipelineId}
                  onChange={handlePipelineSelection}
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                >
                  <option value="">Select Pipeline</option>
                  {pipelines.map((pipeline) => (
                    <option key={pipeline.id} value={pipeline.id}>
                      {pipeline.name}
                    </option>
                  ))}
                </select>

                {(formData.pipelineIds?.length ?? 0) > 0 && (
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-2">
                      {formData.pipelineIds?.map((pipelineId) => {
                        const pipeline = pipelines.find(p => p.id === pipelineId);
                        return (
                          <div
                            key={pipelineId}
                            className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-800"
                          >
                            {pipeline ? pipeline.name : pipelineId}
                            <button
                              type="button"
                              onClick={() => handleRemovePipeline(pipelineId)}
                              className="text-red-500 cursor-pointer"
                            >
                              <img
                                src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                alt="Remove"
                                className="w-4 h-4"
                              />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Filter Buttons</label>
                <select
                  name="filterButton"
                  // value={formData.pipelineId}
                  onChange={handleFilterButtonSelection}
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                >
                  <option value="">Select Filter Button</option>
                  {filterButtons.map((button) => (
                    <option key={button.id} value={button.id}>
                      {button.name}
                    </option>
                  ))}
                </select>

                {(formData.filterButtons?.length ?? 0) > 0 && (
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-2">
                      {formData.filterButtons?.map((buttonId) => {
                        const button = filterButtons.find(b => b.id === buttonId);
                        return (
                          <div
                            key={buttonId}
                            className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-800"
                          >
                            {button ? button.name : buttonId}
                            <button
                              type="button"
                              onClick={() => handleRemoveFilterButton(buttonId)}
                              className="text-red-500 cursor-pointer"
                            >
                              <img
                                src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                alt="Remove"
                                className="w-4 h-4"
                              />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Left Side Panel Items</label>
                <select
                  name="leftSidePanelItems"
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    if (!selectedId) return;

                    setFormData(prev => {
                      const currentItems = prev.leftSidePanelItems || [];

                      // Handle "Select All" option
                      if (selectedId === 'select-all') {
                        const availableItems = leftSidePanelItems.filter(item => {
                          // If the user is not Employee (Staff) or Admin, hide restricted items
                          if (!formData.isAdmin) {
                            const restrictedItems = ['Team Management', 'Settings', 'Migration Beta', 'Audit Logs'];
                            return !restrictedItems.includes(item.name);
                          } else if (!formData.isStaff) {
                            const restrictedItems = ['My Collaborations'];
                            return !restrictedItems.includes(item.name);
                          }
                          return true;
                        });

                        return {
                          ...prev,
                          leftSidePanelItems: availableItems.map(item => item.id)
                        };
                      }

                      // Check if already selected
                      if (currentItems.includes(selectedId)) {
                        showToastMessage('Item already selected', true);
                        return prev;
                      }

                      return {
                        ...prev,
                        leftSidePanelItems: [...currentItems, selectedId]
                      };
                    });
                  }}
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                >
                  <option value="">Select Panel Items</option>
                  {/* Show Select All option only if there are unselected items */}
                  {leftSidePanelItems.filter(item => {
                    // Don't show already selected items
                    if (formData.leftSidePanelItems?.includes(item.id)) return false;

                    // If the user is not Admin, hide restricted items
                    if (!formData.isAdmin) {
                      const restrictedItems = ['Team Management', 'Settings', 'Migration Beta', 'Audit Logs', 'Integrations'];
                      return !restrictedItems.includes(item.name);
                    } else if (!formData.isStaff) {
                      const restrictedItems = ['My Collaborations'];
                      return !restrictedItems.includes(item.name);
                    }
                    return true;
                  }).length > 1 && (
                      <option value="select-all">Select All Available Items</option>
                    )}
                  {leftSidePanelItems
                    .filter(item => {
                      // Don't show already selected items
                      if (formData.leftSidePanelItems?.includes(item.id)) return false;

                      // If the user is not Admin, hide restricted items
                      if (!formData.isAdmin) {
                        const restrictedItems = ['Team Management', 'Settings', 'Migration Beta', 'Audit Logs', 'Integrations'];
                        return !restrictedItems.includes(item.name);
                      } else if (!formData.isStaff) {
                        const restrictedItems = ['My Collaborations'];
                        return !restrictedItems.includes(item.name);
                      }
                      return true;
                    })
                    .map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </select>

                {(formData.leftSidePanelItems?.length ?? 0) > 0 && (
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-2">
                      {formData.leftSidePanelItems
                        ?.map(itemId => {
                          const item = leftSidePanelItems.find(i => i.id === itemId);
                          return {
                            id: itemId,
                            name: item ? item.name : itemId
                          };
                        })
                        .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically by name
                        .map(({ id: itemId, name }) => (
                          <div
                            key={itemId}
                            className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm text-gray-800 ${(() => {
                              const requiredItems = getRequiredPanelItems(formData.isAdmin, formData.isManager, formData.isStaff);
                              return requiredItems.includes(itemId) ? 'bg-blue-100 border border-blue-300' : 'bg-gray-100';
                            })()}`}
                          >
                            {name}
                            {(() => {
                              const requiredItems = getRequiredPanelItems(formData.isAdmin, formData.isManager, formData.isStaff);
                              return requiredItems.includes(itemId) && (
                                <span className="text-blue-600 text-xs" title="Required for selected role(s)">🔒</span>
                              );
                            })()}
                            <button
                              type="button"
                              onClick={() => {
                                // Get required items that cannot be removed
                                const requiredItems = getRequiredPanelItems(formData.isAdmin, formData.isManager, formData.isStaff);

                                // Check if this item is required and cannot be removed
                                if (requiredItems.includes(itemId)) {
                                  const item = leftSidePanelItems.find(i => i.id === itemId);
                                  const itemName = item ? item.name : 'this item';
                                  showToastMessage(`${itemName} cannot be removed as it is required for the selected role(s)`, true);
                                  return;
                                }

                                setFormData(prev => ({
                                  ...prev,
                                  leftSidePanelItems: (prev.leftSidePanelItems ?? []).filter(id => id !== itemId)
                                }));
                              }}
                              className={`cursor-pointer ${(() => {
                                const requiredItems = getRequiredPanelItems(formData.isAdmin, formData.isManager, formData.isStaff);
                                return requiredItems.includes(itemId) ? 'text-gray-400 cursor-not-allowed' : 'text-red-500';
                              })()}`}
                              disabled={(() => {
                                const requiredItems = getRequiredPanelItems(formData.isAdmin, formData.isManager, formData.isStaff);
                                return requiredItems.includes(itemId);
                              })()}
                            >
                              <img
                                src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                alt="Remove"
                                className="w-4 h-4"
                              />
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="col-span-2 mt-8 flex justify-end">
                {/* Add Staff Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-2 bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] text-white rounded-3xl flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Adding Staff...
                    </>
                  ) : (
                    'Add Staff'
                  )}
                </button>

                {/* Cancel Button */}
                <button
                  type="button"
                  onClick={resetFormData}
                  disabled={isSubmitting}
                  className="px-8 py-2 ml-4 border rounded-3xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>
      )}

      {isSubAddPopupOpen && (<div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
        <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative my-8 max-h-[90vh] overflow-y-auto">
          <div className="top-0 left-0 right-0 h-[40px] flex items-center justify-between rounded-tl-[12px] rounded-tr-[12px] p-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                <img src={`${process.env.PUBLIC_URL}/images/desktop/healthicons-staff.svg`} alt="Staff Icon" />
              </div>
              <h2 className="text-xl font-semibold text-black">Add Affiliate Partner</h2>
            </div>
            <div className="text-right">
              <button onClick={() => { setSubAddPopupOpen(false); setPasswordShown("password"); }} className="absolute top-4 right-4 font-semibold text-3xl"
                style={{ background: 'linear-gradient(270deg, #A7C8E7 0%, #00A6D9 100%)', WebkitBackgroundClip: 'text', color: 'transparent', }} >
                <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
              </button>
            </div>
          </div>
          <form onSubmit={handleSubAddSubmit}>
            <div className="grid grid-cols-2 gap-6 mt-8">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={subFormData.name}
                  onChange={handleSubInputChange}
                  placeholder="Enter Full Name"
                  required
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Username</label>
                <div className="flex items-center mt-2">
                  <input
                    type="text"
                    name="username"
                    id="postfix"
                    ref={inputRef}
                    value={subFormData.username}
                    onChange={handleSubInputChange}
                    placeholder="Username"
                    required
                    className="flex-grow p-2 border rounded-l-full"
                    style={{ border: '1px solid #3483BE', borderRight: 'none' }}
                  />
                  <span
                    className="bg-gray-100 p-2 border border-l-0 rounded-r-full text-gray-700"
                    style={{ border: '1px solid #3483BE', borderLeft: 'none' }}
                  >
                    {loginPostfix}
                  </span>
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <input
                  type={passwordShown}
                  name="password"
                  value={subFormData.password}
                  onChange={handleSubInputChange}
                  placeholder="Password"
                  required
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE', }} />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform translate-y-1/5"
                  onClick={() => toogleEyeButton()}
                >
                  <img
                    src={passwordShown === "text" ? `${process.env.PUBLIC_URL}/images/desktop/eye-open.svg` : `${process.env.PUBLIC_URL}/images/desktop/eye-close.svg`}
                    alt={passwordShown === "text" ? "Show Password" : "Hide Password"}
                    className={`w-7 h-7 transition-transform`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={subFormData.email || ''}
                  onChange={handleSubInputChange}
                  placeholder="Email"
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={subFormData.phone}
                  onChange={handleSubInputChange}
                  placeholder="+914756688221"
                  required
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE', }} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Affiliate Partner Permission</label>
                <select
                  name="permissionId"
                  value={subFormData.permissionId}
                  onChange={handleSubInputChange}
                  required
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE', }} >
                  <option value="">Select Affiliate Partner permission</option>
                  {Array.isArray(subAgentPermissions)
                    && subAgentPermissions.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                </select>
              </div>


              <div className="col-span-2 mt-8 flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-8 py-2 text-white rounded-3xl flex items-center justify-center gap-2
      ${isSubmitting
                      ? "bg-gradient-to-b from-gray-400 to-gray-500 cursor-not-allowed opacity-50"
                      : "bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] hover:from-[#FF897A] hover:to-[#F96274]"}`
                  }
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Adding Affiliate Partner...
                    </>
                  ) : (
                    "Add Affiliate Partner"
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => resetFormData()}
                  disabled={isSubmitting}
                  className="px-8 py-2 ml-4 border rounded-3xl disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>
      )}

      {isEditPopupOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative my-8 max-h-[90vh] overflow-y-auto">
            <div className="top-0 left-0 right-0 h-[40px] flex items-center justify-between rounded-tl-[12px] rounded-tr-[12px] p-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                  <img src={`${process.env.PUBLIC_URL}/images/desktop/healthicons-staff.svg`} alt="Staff Icon" />
                </div>
                <h2 className="text-xl font-semibold text-black">Edit Staff</h2>
              </div>
              <button
                onClick={() => {
                  resetFormData();
                  setEditPopupOpen(false);
                }}
                className="absolute top-4 right-4 font-semibold text-3xl"
                style={{ background: 'linear-gradient(270deg, #A7C8E7 0%, #00A6D9 100%)', WebkitBackgroundClip: 'text', color: 'transparent' }}
              >
                <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleEditSubmit}>
              <div className="grid grid-cols-2 gap-6 mt-8">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter Full Name"
                    required
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    disabled
                    className="w-full p-2 border rounded-md mt-2 bg-gray-100"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+914756688221"
                    required
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Staff Designation</label>
                  <select
                    name="staffRoleId"
                    value={formData.staffRoleId}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  >
                    <option value="">Select Staff Designation</option>
                    {Object.entries(staffRoles).map(([roleId, roleName]) => (
                      <option key={roleId} value={roleId}>
                        {roleName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Staff Department</label>
                  <select
                    name="departmentId"
                    value={formData.departmentId}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE', }} >
                    <option value="">Select Staff Department</option>
                    {Array.isArray(departments)
                      && departments.map(dept => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Is this Employee an Admin?</label>
                  <select
                    name="isAdmin"
                    value={formData.isAdmin ? 'yes' : 'no'}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Is this Employee a Manager?</label>
                  <select
                    name="isManager"
                    value={formData.isManager ? 'yes' : 'no'}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Is this Employee a Staff?</label>
                  <select
                    name="isStaff"
                    value={formData.isStaff ? 'yes' : 'no'}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>

                {formData.isStaff && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Select Managers</label>
                    <select
                      name="managerIds"
                      value={formData.managerIds}
                      onChange={handleManagerIdsSelection}
                      className="w-full p-2 border rounded-md mt-2"
                      style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                    >
                      <option value="">Select Manager</option>
                      {userData
                        .filter(user => (user.isAdmin || user.isManager) && user.isActive)
                        .map((manager) => (
                          <option key={manager.id} value={manager.id}>
                            {manager.name}
                          </option>
                        ))}
                    </select>
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-2">
                        {formData.managerIds?.map((managerId) => {
                          const manager = userData.find(u => u.id === managerId);
                          return (
                            <div
                              key={managerId}
                              className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-800"
                            >
                              {manager ? manager.name : managerId}
                              <button
                                type="button"
                                onClick={() => handleRemoveManagerId(managerId)}
                                className="text-red-500 cursor-pointer"
                              >
                                <img
                                  src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                  alt="Remove"
                                  className="w-4 h-4"
                                />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700">Call Attempt Target</label>
                  <input
                    type="number"
                    name="callAttemptTarget"
                    value={formData.callAttemptTarget}
                    onChange={handleInputChange}
                    placeholder="Daily call target"
                    // min="0"
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  />
                </div>

                {/* Pipeline selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Pipeline</label>
                  <select
                    name="pipelineId"
                    // value={formData.pipelineId}
                    onChange={handlePipelineSelection}
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  >
                    <option value="">Select Pipeline</option>
                    {pipelines.map((pipeline) => (
                      <option key={pipeline.id} value={pipeline.id}>
                        {pipeline.name}
                      </option>
                    ))}
                  </select>

                  {(formData.pipelineIds?.length ?? 0) > 0 && (
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-2">
                        {formData.pipelineIds?.map((pipelineId) => {
                          const pipeline = pipelines.find(p => p.id === pipelineId);
                          return (
                            <div
                              key={pipelineId}
                              className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-800"
                            >
                              {pipeline ? pipeline.name : pipelineId}
                              <button
                                type="button"
                                onClick={() => handleRemovePipeline(pipelineId)}
                                className="text-red-500 cursor-pointer"
                              >
                                <img
                                  src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                  alt="Remove"
                                  className="w-4 h-4"
                                />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Filter Buttons</label>
                  <select
                    name="filterButton"
                    // value={formData.pipelineId}
                    onChange={handleFilterButtonSelection}
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  >
                    <option value="">Select Filter Button</option>
                    {filterButtons.map((button) => (
                      <option key={button.id} value={button.id}>
                        {button.name}
                      </option>
                    ))}
                  </select>

                  {(formData.filterButtons?.length ?? 0) > 0 && (
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-2">
                        {formData.filterButtons?.map((buttonId) => {
                          const button = filterButtons.find(b => b.id === buttonId);
                          return (
                            <div
                              key={buttonId}
                              className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-800"
                            >
                              {button ? button.name : buttonId}
                              <button
                                type="button"
                                onClick={() => handleRemoveFilterButton(buttonId)}
                                className="text-red-500 cursor-pointer"
                              >
                                <img
                                  src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                  alt="Remove"
                                  className="w-4 h-4"
                                />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* left side panel items */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Left Side Panel Items</label>
                  <select
                    name="leftSidePanelItems"
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      if (!selectedId) return;

                      setFormData(prev => {
                        const currentItems = prev.leftSidePanelItems || [];

                        // Handle "Select All" option
                        if (selectedId === 'select-all') {
                          const availableItems = leftSidePanelItems.filter(item => {
                            // If the user is not Admin, hide restricted items
                            if (!formData.isAdmin) {
                              const restrictedItems = ['Team Management', 'Settings', 'Migration Beta', 'Audit Logs', 'Integrations'];
                              return !restrictedItems.includes(item.name);
                            } else if (!formData.isStaff) {
                              const restrictedItems = ['My Collaborations'];
                              return !restrictedItems.includes(item.name);
                            }
                            return true;
                          });
                          return {
                            ...prev,
                            leftSidePanelItems: availableItems.map(item => item.id)
                          };
                        }

                        // Check if already selected
                        if (currentItems.includes(selectedId)) {
                          showToastMessage('Item already selected', true);
                          return prev;
                        }

                        return {
                          ...prev,
                          leftSidePanelItems: [...currentItems, selectedId]
                        };
                      });
                    }}
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  >
                    <option value="">Select Panel Items</option>
                    {/* Show Select All option only if there are unselected items */}
                    {leftSidePanelItems.filter(item => {
                      // Don't show already selected items
                      if (formData.leftSidePanelItems?.includes(item.id)) return false;

                      // If the user is not Admin, hide restricted items
                      if (!formData.isAdmin) {
                        const restrictedItems = ['Team Management', 'Settings', 'Migration Beta', 'Audit Logs', 'Integrations'];
                        return !restrictedItems.includes(item.name);
                      } else if (!formData.isStaff) {
                        const restrictedItems = ['My Collaborations'];
                        return !restrictedItems.includes(item.name);
                      }

                      return true;
                    }).length > 1 && (
                        <option value="select-all">Select All Available Items</option>
                      )}
                    {leftSidePanelItems
                      .filter(item => {
                        // Don't show already selected items
                        if (formData.leftSidePanelItems?.includes(item.id)) return false;

                        // If the user is not Admin, hide restricted items
                        if (!formData.isAdmin) {
                          const restrictedItems = ['Team Management', 'Settings', 'Migration Beta', 'Audit Logs', 'Integrations'];
                          return !restrictedItems.includes(item.name);
                        } else if (!formData.isStaff) {
                          const restrictedItems = ['My Collaborations'];
                          return !restrictedItems.includes(item.name);
                        }
                        return true;
                      })
                      .map(item => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                  </select>

                  {(formData.leftSidePanelItems?.length ?? 0) > 0 && (
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-2">
                        {formData.leftSidePanelItems
                          ?.map(itemId => {
                            const item = leftSidePanelItems.find(i => i.id === itemId);
                            return {
                              id: itemId,
                              name: item ? item.name : itemId
                            };
                          })
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(({ id: itemId, name }) => (
                            <div
                              key={itemId}
                              className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm text-gray-800 ${(() => {
                                const requiredItems = getRequiredPanelItems(formData.isAdmin, formData.isManager, formData.isStaff);
                                return requiredItems.includes(itemId) ? 'bg-blue-100 border border-blue-300' : 'bg-gray-100';
                              })()}`}
                            >
                              {name}
                              {(() => {
                                const requiredItems = getRequiredPanelItems(formData.isAdmin, formData.isManager, formData.isStaff);
                                return requiredItems.includes(itemId) && (
                                  <span className="text-blue-600 text-xs" title="Required for selected role(s)">🔒</span>
                                );
                              })()}
                              <button
                                type="button"
                                onClick={() => {
                                  // Get required items that cannot be removed
                                  const requiredItems = getRequiredPanelItems(formData.isAdmin, formData.isManager, formData.isStaff);

                                  // Check if this item is required and cannot be removed
                                  if (requiredItems.includes(itemId)) {
                                    const item = leftSidePanelItems.find(i => i.id === itemId);
                                    const itemName = item ? item.name : 'this item';
                                    showToastMessage(`${itemName} cannot be removed as it is required for the selected role(s)`, true);
                                    return;
                                  }

                                  setFormData(prev => ({
                                    ...prev,
                                    leftSidePanelItems: (prev.leftSidePanelItems ?? []).filter(id => id !== itemId)
                                  }));
                                }}
                                className={`cursor-pointer ${(() => {
                                  const requiredItems = getRequiredPanelItems(formData.isAdmin, formData.isManager, formData.isStaff);
                                  return requiredItems.includes(itemId) ? 'text-gray-400 cursor-not-allowed' : 'text-red-500';
                                })()}`}
                                disabled={(() => {
                                  const requiredItems = getRequiredPanelItems(formData.isAdmin, formData.isManager, formData.isStaff);
                                  return requiredItems.includes(itemId);
                                })()}
                              >
                                <img
                                  src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                                  alt="Remove"
                                  className="w-4 h-4"
                                />
                              </button>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* photo add */}

                <div>
                  <label htmlFor="photo-upload" className="block font-montserrat font-normal text-base text-black mb-2">
                    Photo
                    {pendingPhotoChanges.action === 'upload' && (
                      <span className="ml-2 text-xs text-blue-500">(New photo pending save)</span>
                    )}
                    {pendingPhotoChanges.action === 'delete' && (
                      <span className="ml-2 text-xs text-red-500"></span>
                    )}
                  </label>
                  <div className="relative w-3/4">
                    <div
                      onClick={handlePhotoClick}
                      className="cursor-pointer"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        id="photo-upload"
                        className="hidden"
                        onChange={handlePhotoChange}
                        accept=".jpg,.jpeg,.png"
                        disabled={isLoading}
                      />

                      {previewImage ? (
                        <div className="flex items-center gap-4 p-4 border border-cyan-600 rounded-xl">
                          <div className="w-16 h-16 rounded-full overflow-hidden">
                            <img
                              src={previewImage}
                              alt="Preview"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{photoName}</p>
                            <p className="text-xs text-gray-500">{uploadTime}</p>
                          </div>
                        </div>
                      ) : formData.photo && pendingPhotoChanges.action !== 'delete' ? (
                        <div className="flex items-center gap-4 p-4 border border-cyan-600 rounded-xl">
                          <div className="w-16 h-16 rounded-full overflow-hidden">
                            <img
                              src={photoUrl}
                              alt="Current"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{formData.photo.split('_').slice(1).join('_')}</p>
                            {uploadTime && <p className="text-xs text-gray-500">{uploadTime}</p>}
                          </div>
                        </div>
                      ) : (
                        <div className="w-full py-8 pl-14 pr-4 bg-white border border-cyan-600 rounded-xl hover:bg-gray-50">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
                            <img
                              src={`${process.env.PUBLIC_URL}/images/desktop/catppuccin.svg`}
                              alt="icon"
                              className="w-8 h-8 text-gray-400"
                            />
                          </span>
                          <div className="absolute left-16 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                            {photoName}<br />
                            {uploadTime && <span className="text-gray-500">{uploadTime}</span>}
                          </div>
                        </div>
                      )}
                    </div>

                    {(previewImage || (formData.photo && pendingPhotoChanges.action !== 'delete')) && (
                      <button
                        onClick={handleDeletePhoto}
                        disabled={isLoading}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                <div className="col-span-2 mt-8 flex justify-end">
                  {/* Update Staff Button with Spinner */}
                  <button
                    type="submit"
                    disabled={isUpdating}
                    className="px-8 py-2 bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] text-white rounded-3xl flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdating ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Updating Staff...
                      </>
                    ) : (
                      'Update Staff'
                    )}
                  </button>

                  {/* Cancel Button */}
                  <button
                    type="button"
                    onClick={() => {
                      resetFormData();
                      setEditPopupOpen(false);
                    }}
                    className="px-8 py-2 ml-4 border rounded-3xl disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isUpdating}
                  >
                    Cancel
                  </button>
                </div>

              </div>
            </form>
          </div>
        </div>
      )}

      {isSubEditPopupOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative my-8 max-h-[90vh] overflow-y-auto">
            <div className="top-0 left-0 right-0 h-[40px] flex items-center justify-between rounded-tl-[12px] rounded-tr-[12px] p-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                  <img src={`${process.env.PUBLIC_URL}/images/desktop/healthicons-staff.svg`} alt="Staff Icon" />
                </div>
                <h2 className="text-xl font-semibold text-black">Edit Affiliate Partner</h2>
              </div>
              <button
                onClick={() => {
                  resetFormData();
                }}
                className="absolute top-4 right-4 font-semibold text-3xl"
                style={{ background: 'linear-gradient(270deg, #A7C8E7 0%, #00A6D9 100%)', WebkitBackgroundClip: 'text', color: 'transparent' }}
              >
                <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubEditSubmit}>
              <div className="grid grid-cols-2 gap-6 mt-8">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={subFormData.name}
                    onChange={handleSubInputChange}
                    placeholder="Enter Full Name"
                    required
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Username</label>
                  <div className="flex items-center mt-2">
                    <input
                      type="text"
                      name="username"
                      id="postfix"
                      ref={inputRef}
                      value={subFormData.username}
                      onChange={handleSubInputChange}
                      placeholder="Username"
                      required
                      className="flex-grow p-2 border rounded-l-full"
                      style={{ border: '1px solid #3483BE', borderRight: 'none' }}
                    />
                    <span
                      className="bg-gray-100 p-2 border border-l-0 rounded-r-full text-gray-700"
                      style={{ border: '1px solid #3483BE', borderLeft: 'none' }}
                    >
                      {loginPostfix}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={subFormData.email || ''}
                    onChange={handleSubInputChange}
                    placeholder="Email"
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={subFormData.phone}
                    onChange={handleSubInputChange}
                    placeholder="+914756688221"
                    required
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE', }} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Affiliate Partner Permission</label>
                  <select
                    name="permissionId"
                    value={subFormData.permissionId}
                    onChange={handleSubInputChange}
                    required
                    className="w-full p-2 border rounded-md mt-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE', }} >
                    <option value="">Select Affiliate Partner permission</option>
                    {Array.isArray(subAgentPermissions)
                      && subAgentPermissions.map(dept => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="col-span-2 mt-8 flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-8 py-2 text-white rounded-3xl flex items-center justify-center gap-2
      ${isSubmitting
                        ? "bg-gradient-to-b from-gray-400 to-gray-500 cursor-not-allowed opacity-50"
                        : "bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] hover:from-[#FF897A] hover:to-[#F96274]"}`
                    }
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Updating Affiliate Partner...
                      </>
                    ) : (
                      "Update Affiliate Partner"
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => resetFormData()}
                    disabled={isSubmitting}
                    className="px-8 py-2 ml-4 border rounded-3xl disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>

              </div>
            </form>
          </div>
        </div>
      )}

      {emailResetPopupOpen && (<div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative">
          <div className=" top-0 left-0 right-0 h-[40px] flex items-center justify-between rounded-tl-[12px] rounded-tr-[12px] p-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                <img src={`${process.env.PUBLIC_URL}/images/desktop/healthicons-staff.svg`} alt="Staff Icon" />
              </div>
              <h2 className="text-xl font-semibold text-black">Edit Staff Email</h2>
            </div>
            <div className="text-right">
              <button onClick={() => { setEmailResetPopupOpen(false); setUpdatedEmail(''); resetFormData(); }} className="absolute top-4 right-4 font-semibold text-3xl"
                style={{ background: 'linear-gradient(270deg, #A7C8E7 0%, #00A6D9 100%)', WebkitBackgroundClip: 'text', color: 'transparent', }} >
                <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />  </button>
            </div>
          </div>
          <form onSubmit={handleEditSubmit}>
            <div className="grid grid-cols-2 gap-6 mt-8">
              <div>
                <label className="block text-sm font-medium text-gray-700">Current Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  placeholder="Email"
                  required
                  readOnly
                  className="w-full p-2 border rounded-md mt-2 bg-slate-200"
                  style={{ borderRadius: '24px', border: '1px solid #d4d2cd', }} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">New Email</label>
                <input
                  type="text"
                  name="email"
                  value={updatedEmail}
                  onChange={handleCredentialChange}
                  placeholder="New Email"
                  autoComplete='off'
                  required
                  className="w-full p-2 border rounded-md mt-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE', }} />
              </div>
              <div className="col-span-2 mt-8 flex justify-end">
                <button
                  className={`px-8 py-2 rounded-3xl text-white flex items-center justify-center gap-2 ${isDisabled
                    ? "bg-gradient-to-b from-gray-400 to-gray-500 cursor-not-allowed opacity-50"
                    : "bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] hover:from-[#FF897A] hover:to-[#F96274]"
                    }`}
                  disabled={isDisabled}
                  onClick={(e) => handleEmailEditSubmit(e)}
                >
                  {isDisabled ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Updating...
                    </>
                  ) : (
                    "Update"
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setEmailResetPopupOpen(false);
                    setUpdatedEmail('');
                    resetFormData();
                  }}
                  disabled={isDisabled}
                  className="px-8 py-2 ml-4 border rounded-3xl disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>
      )}

      {passwordResetPopupOpen && (<div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative">
          <div className=" top-0 left-0 right-0 h-[40px] flex items-center justify-between rounded-tl-[12px] rounded-tr-[12px] p-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                <img src={`${process.env.PUBLIC_URL}/images/desktop/healthicons-staff.svg`} alt="Staff Icon" />
              </div>
              <h2 className="text-xl font-semibold text-black">Edit Staff Password</h2>
            </div>
            <div className="text-right">
              <button onClick={() => { setPasswordResetPopupOpen(false); setUpdatedPassword(''); resetFormData(); }} className="absolute top-4 right-4 font-semibold text-3xl"
                style={{ background: 'linear-gradient(270deg, #A7C8E7 0%, #00A6D9 100%)', WebkitBackgroundClip: 'text', color: 'transparent', }} >
                <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />  </button>
            </div>
          </div>
          <form onSubmit={handleEditSubmit}>
            <div className="grid grid-cols-2 gap-6 mt-8">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  placeholder="Email"
                  required
                  readOnly
                  className="w-full p-2 border rounded-md mt-2 bg-slate-200"
                  style={{ borderRadius: '24px', border: '1px solid #d4d2cd', }} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Set New Password
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="password"
                    value={updatedPassword}
                    onChange={handleCredentialChange}
                    placeholder="New Password"
                    autoComplete="off"
                    required
                    className="w-full p-2 border rounded-full pl-4 pr-24 mt-2"
                    style={{
                      borderRadius: "24px",
                      border: "1px solid #3483BE",
                    }}
                  />
                  <button
                    type="button"
                    onClick={autoGeneratePassword} // Function to generate a password
                    className="absolute top-[38%] right-[9.5%] transform -translate-y-1/4 bg-blue-500 opacity-70 text-white font-medium px-4 py-2 rounded-s-full hover:bg-blue-600 hover:opacity-90"
                  >
                    <img src={`${process.env.PUBLIC_URL}/images/desktop/refresh&generate.svg`} alt="Close" className="w-6 h-6" />
                  </button>
                  <button
                    type="button"
                    onClick={copyPassword} // Function to copy the generated password
                    className="absolute top-[38%] right-0 transform -translate-y-1/4 bg-green-500 opacity-70 text-white font-medium px-4 py-2 rounded-e-full hover:bg-green-600 hover:opacity-90"
                    disabled={updatedPassword === ''}
                  >
                    <img src={`${process.env.PUBLIC_URL}/images/desktop/copy-svg.svg`} alt="Close" className="w-6 h-6" />
                  </button>
                </div>
                {copySuccess && (
                  <p
                    className={`text-green-500 mt-2 transition-opacity duration-1000 ${fadeOut ? "opacity-0" : "opacity-100"
                      }`}
                  >
                    {copySuccess}
                  </p>
                )}
              </div>

              <div className="col-span-2 mt-8 flex justify-end">
                <button
                  className={`px-8 py-2 rounded-3xl text-white flex items-center justify-center gap-2 ${isDisabled
                    ? "bg-gradient-to-b from-gray-400 to-gray-500 cursor-not-allowed opacity-50"
                    : "bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] hover:from-[#FF897A] hover:to-[#F96274]"
                    }`}
                  disabled={isDisabled}
                  onClick={(e) => handlePasswordEditSubmit(e)}
                >
                  {isDisabled ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Updating...
                    </>
                  ) : (
                    "Update"
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setPasswordResetPopupOpen(false);
                    setUpdatedPassword('');
                    resetFormData();
                  }}
                  disabled={isDisabled}
                  className="px-8 py-2 ml-4 border rounded-3xl disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>
      )}

      {subPasswordResetPopupOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                  <img src={`${process.env.PUBLIC_URL}/images/desktop/password.svg`} alt="Password" className="w-8 h-8 invert" />
                </div>
                <h2 className="text-xl font-semibold">{subFormData.name} - Update Password</h2>
              </div>
              <button type="button" onClick={() => { setSubPasswordResetPopupOpen(false); setUpdatedPassword(''); resetFormData(); }} className="text-gray-500">
                <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6 mt-8">
              <div>
                <label className="block text-sm font-medium text-gray-700">Username</label>
                <div className="flex items-center mt-2">
                  <input
                    type="text"
                    name="username"
                    value={subFormData.username}
                    readOnly
                    className="flex-grow p-2 border rounded-l-full opacity-70"
                    style={{ border: '1px solid #3483BE', borderRight: 'none' }}
                  />
                  <span
                    className="bg-gray-100 p-2 border border-l-0 rounded-r-full text-gray-700 opacity-70"
                    style={{ border: '1px solid #3483BE', borderLeft: 'none' }}
                  >
                    {loginPostfix}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Set New Password
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="password"
                    value={updatedPassword}
                    onChange={handleCredentialChange}
                    placeholder="New Password"
                    autoComplete="off"
                    required
                    className="w-full p-2 border rounded-full pl-4 pr-24 mt-2"
                    style={{
                      borderRadius: "24px",
                      border: "1px solid #3483BE",
                    }}
                  />
                  <button
                    type="button"
                    onClick={autoGeneratePassword}
                    className="absolute top-[38%] right-[9.5%] transform -translate-y-1/4 bg-blue-500 opacity-70 text-white font-medium px-4 py-2 rounded-s-full hover:bg-blue-600 hover:opacity-90"
                  >
                    <img src={`${process.env.PUBLIC_URL}/images/desktop/refresh&generate.svg`} alt="Generate" className="w-6 h-6" />
                  </button>
                  <button
                    type="button"
                    onClick={copyPassword}
                    className="absolute top-[38%] right-0 transform -translate-y-1/4 bg-green-500 opacity-70 text-white font-medium px-4 py-2 rounded-e-full hover:bg-green-600 hover:opacity-90"
                    disabled={updatedPassword === ''}
                  >
                    <img src={`${process.env.PUBLIC_URL}/images/desktop/copy-svg.svg`} alt="Copy" className="w-6 h-6" />
                  </button>
                </div>
                {copySuccess && (
                  <p
                    className={`text-green-500 mt-2 transition-opacity duration-1000 ${fadeOut ? "opacity-0" : "opacity-100"
                      }`}
                  >
                    {copySuccess}
                  </p>
                )}
              </div>

              <div className="col-span-2 mt-8 flex justify-end">
                {/* Update Button with Spinner and Dynamic Styling */}
                <button
                  className={`px-8 py-2 rounded-3xl text-white flex items-center justify-center gap-2 ${isDisabled
                    ? "bg-gradient-to-b from-gray-400 to-gray-500 cursor-not-allowed opacity-50"
                    : "bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] hover:from-[#FF897A] hover:to-[#F96274]"
                    }`}
                  disabled={isDisabled}
                  onClick={(e) => handleSubPasswordEditSubmit(e)}
                >
                  {isDisabled ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Updating...
                    </>
                  ) : (
                    "Update"
                  )}
                </button>

                {/* Cancel Button */}
                <button
                  type="button"
                  onClick={() => {
                    setSubPasswordResetPopupOpen(false);
                    setUpdatedPassword('');
                    resetFormData();
                  }}
                  className="px-8 py-2 ml-4 border rounded-3xl disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isDisabled}
                >
                  Cancel
                </button>
              </div>

            </div>
          </div>
        </div>
      )}


      {subEmailResetPopupOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                  <img src={`${process.env.PUBLIC_URL}/images/desktop/email.svg`} alt="Email" className="w-8 h-8 invert" />
                </div>
                <h2 className="text-xl font-semibold">{subFormData.name} - Update Email</h2>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSubEmailResetPopupOpen(false);
                  setUpdatedEmail('');
                  resetFormData();
                }}
                className="text-gray-500"
              >
                <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6 mt-8">
              <div>
                <label className="block text-sm font-medium text-gray-700">Current Email</label>
                <input
                  type="email"
                  name="email"
                  value={subFormData.email || ''}
                  readOnly
                  className="w-full p-2 border rounded-full mt-2 bg-gray-50 opacity-70"
                  style={{
                    borderRadius: "24px",
                    border: "1px solid #3483BE",
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">New Email</label>
                <input
                  type="email"
                  name="email"
                  value={updatedEmail}
                  onChange={handleCredentialChange}
                  placeholder="Enter new email"
                  required
                  className="w-full p-2 border rounded-full mt-2"
                  style={{
                    borderRadius: "24px",
                    border: "1px solid #3483BE",
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Current Password</label>
                <input
                  type={passwordShown}
                  name="password"
                  value={subFormData.password}
                  onChange={handleSubInputChange}
                  placeholder="Enter current password"
                  required
                  className="w-full p-2 border rounded-full mt-2"
                  style={{
                    borderRadius: "24px",
                    border: "1px solid #3483BE",
                  }}
                />
              </div>

              <div className="col-span-2 mt-8 flex justify-end">
                <button
                  className={`px-8 py-2 rounded-3xl text-white flex items-center justify-center gap-2 ${
                    isDisabled
                      ? "bg-gradient-to-b from-gray-400 to-gray-500 cursor-not-allowed opacity-50"
                      : "bg-gradient-to-b from-[#FF7B6F] to-[#F9536B] hover:from-[#FF897A] hover:to-[#F96274]"
                  }`}
                  disabled={isDisabled}
                  onClick={(e) => handleSubEmailEditSubmit(e)}
                >
                  {isDisabled ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Updating...
                    </>
                  ) : (
                    "Update Email"
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setSubEmailResetPopupOpen(false);
                    setUpdatedEmail('');
                    resetFormData();
                  }}
                  className="px-8 py-2 ml-4 border rounded-3xl disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isDisabled}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showToast && (
        <Toast
          message={toastMessage}
          isError={
            toastMessage.includes('Error') ||
            toastMessage.includes('Cannot') ||
            toastMessage.includes('Failed')
          }
          show={showToast}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default StaffManagement;
